import React, { useState, useEffect } from 'react';
import { api } from './api';
import TransactionImportExport from './components/TransactionImportExport';

const BudgetSystem = ({ user, isBoard = false, subscriptionTier = 'basic' }) => {
  const [activeView, setActiveView] = useState('overview');
  const [budgetConfig, setBudgetConfig] = useState(null);
  const [budgetSummary, setBudgetSummary] = useState(null);
  const [budgetItems, setBudgetItems] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [balanceSheet, setBalanceSheet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Modal states
  const [showAddBudgetItem, setShowAddBudgetItem] = useState(false);
  const [showAddTransaction, setShowAddTransaction] = useState(false);
  const [showAddBalanceItem, setShowAddBalanceItem] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [showSetBudgetModal, setShowSetBudgetModal] = useState(false);
  const [showRolloverModal, setShowRolloverModal] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [restorePoints, setRestorePoints] = useState([]);
  const [showProposedColumn, setShowProposedColumn] = useState(false);  // Toggle for Proposed Budget column

  // Form states
  const [budgetItemForm, setBudgetItemForm] = useState({
    category: 'expense',
    subcategory: '',
    description: '',
    budgeted_amount: '',
    recurrence: 'monthly',
    notes: '',
    auto_calc: true,  // For dues items: auto-calculate from unit count
    proposed_amount: ''  // Proposed budget amount
  });

  const [transactionForm, setTransactionForm] = useState({
    category: 'expense',
    subcategory: '',
    description: '',
    amount: '',
    transaction_date: new Date().toISOString().split('T')[0],
    vendor: '',
    reference_number: '',
    notes: '',
    budget_line_item_id: '',
    property_address: '',
    property_identifier: ''
  });

  const [balanceItemForm, setBalanceItemForm] = useState({
    item_type: 'asset',
    category: 'current_assets',
    name: '',
    amount: '',
    as_of_date: new Date().toISOString().split('T')[0],
    notes: ''
  });

  const [configForm, setConfigForm] = useState({
    budget_year: new Date().getFullYear(),
    total_units: 100,
    dues_per_unit_monthly: 250.0,
    notes: '',
    period_override: false,
    period_months: 12
  });

  const [setBudgetForm, setSetBudgetForm] = useState({
    budget_type: 'initial',
    fiscal_year: new Date().getFullYear().toString(),
    start_date: budgetConfig?.start_date || new Date().toISOString().split('T')[0],
    end_date: budgetConfig?.end_date || new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
    notes: '',
    show_prior_year: false
  });

  const [rolloverAction, setRolloverAction] = useState('keep_actuals');

  useEffect(() => {
    fetchBudgetData();
  }, []);

  const fetchBudgetData = async () => {
    setLoading(true);
    setError('');
    try {
      // Fetch budget configuration
      const configResponse = await api.get('/api/budget/config');
      if (configResponse.data.success) {
        setBudgetConfig(configResponse.data.config);
        setConfigForm(configResponse.data.config);
      }

      // Fetch budget summary
      const summaryResponse = await api.get('/api/budget/summary');
      if (summaryResponse.data.success) {
        setBudgetSummary(summaryResponse.data.summary);
      }

      // Fetch budget items
      const itemsResponse = await api.get('/api/budget/items');
      if (itemsResponse.data.success) {
        setBudgetItems(itemsResponse.data.items);
      }

      // Fetch transactions
      const transactionsResponse = await api.get('/api/budget/transactions');
      if (transactionsResponse.data.success) {
        setTransactions(transactionsResponse.data.transactions);
      }

      // Fetch balance sheet
      const balanceResponse = await api.get('/api/budget/balance-sheet');
      if (balanceResponse.data.success) {
        setBalanceSheet(balanceResponse.data.balance_sheet);
      }

    } catch (error) {
      console.error('Error fetching budget data:', error);
      setError('Failed to load budget information. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBudgetItem = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      // Determine if this is a dues item
      const isDuesItem = budgetItemForm.subcategory === 'dues';
      
      // Build request data with proper type conversion
      const requestData = {
        category: budgetItemForm.category,
        subcategory: budgetItemForm.subcategory,
        description: budgetItemForm.description,
        budgeted_amount: parseFloat(budgetItemForm.budgeted_amount) || 0,
        recurrence: budgetItemForm.recurrence,
        notes: budgetItemForm.notes || '',
        // Only include auto_calc for dues items, otherwise set to false
        auto_calc: isDuesItem ? (budgetItemForm.auto_calc === true) : false,
        // Always include proposed_amount if provided
        proposed_amount: budgetItemForm.proposed_amount ? parseFloat(budgetItemForm.proposed_amount) : null
      };
      
      console.log('Creating/updating budget item with:', requestData);
      
      let response;
      if (editingItem) {
        // Update existing item
        response = await api.put(`/api/budget/items/${editingItem.id}`, requestData);
      } else {
        // Create new item
        response = await api.post('/api/budget/items', requestData);
      }
      
      if (response.data.success) {
        setShowAddBudgetItem(false);
        setEditingItem(null);
        setBudgetItemForm({
          category: 'expense',
          subcategory: '',
          description: '',
          budgeted_amount: '',
          recurrence: 'monthly',
          notes: '',
          auto_calc: true,
          proposed_amount: ''
        });
        await fetchBudgetData();
        alert(`Budget item ${editingItem ? 'updated' : 'created'} successfully!`);
      }
    } catch (error) {
      console.error('Error with budget item:', error);
      alert(`Failed to ${editingItem ? 'update' : 'create'} budget item: ` + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSetBudget = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await api.post('/api/budget-management/set-budget', setBudgetForm);
      
      if (response.data.success) {
        alert(`Budget document created successfully!\nDocument ID: ${response.data.document_id}\nCategory: ${response.data.category}`);
        setShowSetBudgetModal(false);
        fetchBudgetData();
      }
    } catch (error) {
      console.error('Error creating budget document:', error);
      alert('Failed to create budget document: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleRolloverBudget = async () => {
    try {
      setLoading(true);
      
      // First, get rollover info to show confirmation
      const infoResponse = await api.get('/api/budget/config');
      const currentConfig = infoResponse.data.config;  // Access the config object
      
      const action = rolloverAction === 'clear_actuals' ? 'clear' : 'keep';
      const actionDesc = action === 'clear' 
        ? 'Use Proposed Budget values for new period CY Budget' 
        : 'Keep current CY Budget values in new period';
      
      const fromYear = currentConfig?.budget_year || 'Unknown';
      const toYear = typeof currentConfig?.budget_year === 'number' ? currentConfig.budget_year + 1 : 'Unknown';
      
      if (!window.confirm(
        `BUDGET ROLLOVER CONFIRMATION\n\n` +
        `Rolling from: ${fromYear} period\n` +
        `Rolling to: ${toYear} period\n\n` +
        `Action: ${actionDesc}\n\n` +
        `This will:\n` +
        `• Move CY Budget → PY Budget\n` +
        `• Move CY Actual → PY Actual (calculated from transactions)\n` +
        `• ${actionDesc}\n` +
        `• Carry forward cash balance\n` +
        `• Preserve all transactions for historical reports\n\n` +
        `A restore point will be created. Proceed?`
      )) {
        setLoading(false);
        return;
      }
      
      // Call correct endpoint with action parameter
      const response = await api.post('/api/budget/rollover', null, {
        params: { action }
      });
      
      if (response.data.success) {
        const info = response.data.rollover_info;
        alert(
          `✅ Budget Rolled Over Successfully!\n\n` +
          `From: ${info.rolling_from.year} (${info.rolling_from.months} months)\n` +
          `To: ${info.rolling_to.year} (${info.rolling_to.months} months)\n\n` +
          `${response.data.items_created} budget items copied\n\n` +
          `Restore Point ID: ${response.data.restore_point_id}\n` +
          `(You can undo this in Budget Items if needed)`
        );
        setShowRolloverModal(false);
        await fetchBudgetData();
        await fetchRestorePoints(); // Refresh restore points list
      }
    } catch (error) {
      console.error('Error rolling over budget:', error);
      alert('Failed to rollover budget: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSyncActuals = async () => {
    if (!window.confirm('Sync actuals from transactions?\n\nThis will recalculate all budget item actuals by summing their linked transactions. Any mismatches will be corrected.')) {
      return;
    }

    try {
      setLoading(true);
      const response = await api.post('/api/budget/sync-actuals');
      
      if (response.data.success) {
        const { synced_count, total_adjustment } = response.data;
        alert(`✅ Sync Complete!\n\n${synced_count} budget items updated\nTotal adjustment: $${total_adjustment.toFixed(2)}`);
        await fetchBudgetData();
      }
    } catch (error) {
      console.error('Error syncing actuals:', error);
      alert('Failed to sync actuals: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const fetchRestorePoints = async () => {
    try {
      setLoading(true);
      // Use the ORIGINAL save-points endpoint from budget-management
      const response = await api.get('/api/budget-management/save-points');
      if (response.data.success) {
        setRestorePoints(response.data.save_points || []);
      }
    } catch (error) {
      console.error('Error fetching save points:', error);
      setRestorePoints([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenRestoreModal = async () => {
    await fetchRestorePoints();
    setShowRestoreModal(true);
  };

  const handleRestoreBudget = async (restorePointId, confirmed = false) => {
    try {
      setLoading(true);
      // Use the ORIGINAL restore endpoint from budget-management
      const response = await api.post(`/api/budget-management/restore/${restorePointId}?confirm=${confirmed}`);
      
      // Check if confirmation is required (there are extra items)
      if (response.data.requires_confirmation) {
        const proceed = window.confirm(
          `⚠️ Warning: ${response.data.message}\n\n` +
          `${response.data.warning}\n\n` +
          `Items that will be deactivated (${response.data.extra_items_count}):\n` +
          `${response.data.extra_items_preview?.join('\n') || 'None'}\n\n` +
          `Save point contains ${response.data.save_point_items} items.\n\n` +
          `Do you want to proceed? The deactivated items can be recovered later.`
        );
        
        if (proceed) {
          // Call again with confirm=true
          await handleRestoreBudget(restorePointId, true);
        }
        return;
      }
      
      if (response.data.success) {
        let message = `✅ Budget Restored!\n\n${response.data.message}`;
        if (response.data.items_deactivated > 0) {
          message += `\n\n${response.data.items_deactivated} newer items were deactivated (not deleted).`;
        }
        alert(message);
        setShowRestoreModal(false);
        await fetchBudgetData();
        await fetchRestorePoints(); // Refresh restore points list
      }
    } catch (error) {
      console.error('Error restoring budget:', error);
      alert('Failed to restore budget: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTransaction = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await api.post('/api/budget/transactions', {
        ...transactionForm,
        amount: parseFloat(transactionForm.amount),
        transaction_date: transactionForm.transaction_date // Send as YYYY-MM-DD, not ISO UTC
      });
      
      if (response.data.success) {
        setShowAddTransaction(false);
        setEditingTransaction(null);
        setTransactionForm({
          category: 'expense',
          subcategory: '',
          description: '',
          amount: '',
          transaction_date: new Date().toISOString().split('T')[0],
          vendor: '',
          reference_number: '',
          notes: '',
          budget_line_item_id: '',
          property_address: '',
          property_identifier: ''
        });
        await fetchBudgetData();
        if (response.data.period_warning) {
          alert('Transaction recorded successfully!\n\nNote: ' + response.data.period_warning);
        } else {
          alert('Transaction recorded successfully!');
        }
      } else {
        // Handle success: false responses (e.g., period validation)
        alert('Could not record transaction: ' + (response.data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error creating transaction:', error);
      alert('Failed to record transaction: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleEditTransaction = (transaction) => {
    setEditingTransaction(transaction);
    setTransactionForm({
      category: transaction.category || 'expense',
      subcategory: transaction.subcategory || '',
      description: transaction.description || '',
      amount: transaction.amount || '',
      transaction_date: transaction.transaction_date ? new Date(transaction.transaction_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      vendor: transaction.vendor || '',
      reference_number: transaction.reference_number || '',
      notes: transaction.notes || '',
      budget_line_item_id: transaction.budget_line_item_id || '',
      property_address: transaction.property_address || '',
      property_identifier: transaction.property_identifier || ''
    });
    setShowAddTransaction(true);
  };

  const handleDeleteTransaction = async (transactionId) => {
    if (!window.confirm('Are you sure you want to delete this transaction? This action cannot be undone.')) {
      return;
    }
    
    try {
      setLoading(true);
      const response = await api.delete(`/api/budget/transactions/${transactionId}`);
      
      if (response.data.success) {
        await fetchBudgetData();
      }
    } catch (error) {
      console.error('Error deleting transaction:', error);
      alert('Failed to delete transaction: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTransaction = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await api.put(`/api/budget/transactions/${editingTransaction.id}`, {
        ...transactionForm,
        amount: parseFloat(transactionForm.amount),
        transaction_date: transactionForm.transaction_date // Send as YYYY-MM-DD, not ISO UTC
      });
      
      if (response.data.success) {
        setShowAddTransaction(false);
        setEditingTransaction(null);
        setTransactionForm({
          category: 'expense',
          subcategory: '',
          description: '',
          amount: '',
          transaction_date: new Date().toISOString().split('T')[0],
          vendor: '',
          reference_number: '',
          notes: '',
          budget_line_item_id: '',
          property_address: '',
          property_identifier: ''
        });
        await fetchBudgetData();
        alert('Transaction updated successfully!');
      }
    } catch (error) {
      console.error('Error updating transaction:', error);
      alert('Failed to update transaction: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBalanceItem = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await api.post('/api/budget/balance-sheet/items', {
        ...balanceItemForm,
        amount: parseFloat(balanceItemForm.amount),
        as_of_date: new Date(balanceItemForm.as_of_date).toISOString()
      });
      
      if (response.data.success) {
        setShowAddBalanceItem(false);
        setBalanceItemForm({
          item_type: 'asset',
          category: 'current_assets',
          name: '',
          amount: '',
          as_of_date: new Date().toISOString().split('T')[0],
          notes: ''
        });
        await fetchBudgetData();
        alert('Balance sheet item created successfully!');
      }
    } catch (error) {
      console.error('Error creating balance sheet item:', error);
      alert('Failed to create balance sheet item: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const initializeDefaults = async () => {
    try {
      setLoading(true);
      const response = await api.post('/api/budget/initialize-defaults');
      if (response.data.success) {
        await fetchBudgetData();
        alert('Default budget categories created successfully!');
      }
    } catch (error) {
      console.error('Error initializing defaults:', error);
      alert('Failed to initialize defaults: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  if (loading && !budgetConfig) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="text-center py-8">
          <svg className="mx-auto h-12 w-12 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01" />
          </svg>
          <p className="mt-2 text-sm text-red-600">{error}</p>
          <button
            onClick={() => { setError(''); fetchBudgetData(); }}
            className="mt-4 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md text-sm"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-100 rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              📊 Budget & Financial Management
            </h1>
            <p className="text-gray-600 text-base">
              {isBoard ? 'Manage HOA budget, expenses, and financial planning' : 'View community budget and financial reports'}
            </p>
          </div>
          {isBoard && (
            <div className="flex space-x-2">
              {budgetItems.length === 0 && (
                <button
                  onClick={initializeDefaults}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium"
                >
                  Initialize Defaults
                </button>
              )}
            </div>
          )}
        </div>

        {/* Navigation Tabs */}
        <div className="mt-6 border-b border-gray-200">
          <nav className="-mb-px flex space-x-4 md:space-x-8 overflow-x-auto scrollbar-hide pb-2 md:pb-0">
            <button
              onClick={() => setActiveView('overview')}
              className={`py-2 px-3 border-b-2 font-medium text-sm whitespace-nowrap flex-shrink-0 ${
                activeView === 'overview'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Budget Overview
            </button>
            <button
              onClick={() => setActiveView('budget-items')}
              className={`py-2 px-3 border-b-2 font-medium text-sm whitespace-nowrap flex-shrink-0 ${
                activeView === 'budget-items'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Budget Items
            </button>
            <button
              onClick={() => setActiveView('transactions')}
              className={`py-2 px-3 border-b-2 font-medium text-sm whitespace-nowrap flex-shrink-0 ${
                activeView === 'transactions'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Transactions
            </button>
            <button
              onClick={() => setActiveView('balance-sheet')}
              className={`py-2 px-3 border-b-2 font-medium text-sm whitespace-nowrap flex-shrink-0 ${
                activeView === 'balance-sheet'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Balance Sheet
            </button>
            <button
              onClick={() => {
                if (subscriptionTier === 'basic') {
                  alert('Reports are available in Standard and Premium plans. Upgrade to access this feature.');
                  return;
                }
                setActiveView('reports');
              }}
              disabled={subscriptionTier === 'basic'}
              className={`py-2 px-3 border-b-2 font-medium text-sm whitespace-nowrap flex-shrink-0 ${
                subscriptionTier === 'basic'
                  ? 'border-transparent text-gray-400 cursor-not-allowed opacity-50'
                  : activeView === 'reports'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Reports {subscriptionTier === 'basic' && <span className="text-xs ml-1">(Standard+)</span>}
            </button>
          </nav>
        </div>
      </div>

      {/* Content Views */}
      {activeView === 'overview' && (
        <BudgetOverview
          summary={budgetSummary}
          config={budgetConfig}
          isBoard={isBoard}
        />
      )}

      {activeView === 'budget-items' && (
        <BudgetItemsView
          items={budgetItems}
          config={budgetConfig}
          isBoard={isBoard}
          onAddItem={() => setShowAddBudgetItem(true)}
          onEditItem={(item) => {
            setEditingItem(item);
            // Safely convert all values to proper types to avoid "object object" errors
            const safeString = (val) => {
              if (val === null || val === undefined) return '';
              if (typeof val === 'object') return JSON.stringify(val);
              return String(val);
            };
            const safeNumber = (val) => {
              if (val === null || val === undefined) return '';
              const num = parseFloat(val);
              return isNaN(num) ? '' : num.toString();
            };
            setBudgetItemForm({
              category: safeString(item.category) || 'expense',
              subcategory: safeString(item.subcategory) || '',
              description: safeString(item.description) || '',
              budgeted_amount: safeNumber(item.budgeted_amount),
              recurrence: safeString(item.recurrence) || 'monthly',
              notes: safeString(item.notes) || '',
              auto_calc: item.auto_calc !== false,  // Default true if not set
              proposed_amount: safeNumber(item.proposed_amount)
            });
            setShowAddBudgetItem(true);
          }}
          onRefresh={fetchBudgetData}
          setShowSetBudgetModal={setShowSetBudgetModal}
          setShowRolloverModal={setShowRolloverModal}
          onOpenRestoreModal={handleOpenRestoreModal}
          onSetBudget={handleSetBudget}
          onRolloverBudget={handleRolloverBudget}
          onSyncActuals={handleSyncActuals}
          setBudgetForm={setBudgetForm}
          showProposedColumn={showProposedColumn}
          setShowProposedColumn={setShowProposedColumn}
          rolloverAction={rolloverAction}
          loading={loading}
          setLoading={setLoading}
        />
      )}

      {activeView === 'transactions' && (
        <TransactionsView
          transactions={transactions}
          budgetItems={budgetItems}
          isBoard={isBoard}
          user={user}
          onAddTransaction={() => {
            setEditingTransaction(null);
            setTransactionForm({
              category: 'expense',
              subcategory: '',
              description: '',
              amount: '',
              transaction_date: new Date().toISOString().split('T')[0],
              vendor: '',
              reference_number: '',
              notes: '',
              budget_line_item_id: '',
              property_address: '',
              property_identifier: ''
            });
            setShowAddTransaction(true);
          }}
          onEditTransaction={handleEditTransaction}
          onDeleteTransaction={handleDeleteTransaction}
          onRefresh={fetchBudgetData}
        />
      )}

      {activeView === 'balance-sheet' && (
        <BalanceSheetView
          balanceSheet={balanceSheet}
          isBoard={isBoard}
          onAddItem={() => setShowAddBalanceItem(true)}
          onRefresh={fetchBudgetData}
        />
      )}

      {activeView === 'reports' && (
        <ReportsView
          budgetSummary={budgetSummary}
          transactions={transactions}
          balanceSheet={balanceSheet}
          budgetItems={budgetItems}
          config={budgetConfig}
          isBoard={isBoard}
        />
      )}

      {/* Add/Edit Budget Item Modal */}
      {showAddBudgetItem && (
        <BudgetItemModal
          form={budgetItemForm}
          setForm={setBudgetItemForm}
          onSubmit={handleCreateBudgetItem}
          onClose={() => {
            setShowAddBudgetItem(false);
            setEditingItem(null);
            setBudgetItemForm({
              category: 'expense',
              subcategory: '',
              description: '',
              budgeted_amount: '',
              recurrence: 'monthly',
              notes: '',
              auto_calc: true,
              proposed_amount: ''
            });
          }}
          loading={loading}
          isEdit={!!editingItem}
          config={budgetConfig}
        />
      )}


      {/* Set Budget Modal */}
      {showSetBudgetModal && (
        <SetBudgetModal
          form={setBudgetForm}
          setForm={setSetBudgetForm}
          onSubmit={handleSetBudget}
          onClose={() => setShowSetBudgetModal(false)}
          loading={loading}
          config={budgetConfig}
        />
      )}

      {/* Rollover Budget Modal */}
      {showRolloverModal && (
        <RolloverBudgetModal
          action={rolloverAction}
          setAction={setRolloverAction}
          onConfirm={handleRolloverBudget}
          onClose={() => setShowRolloverModal(false)}
          loading={loading}
        />
      )}

      {/* Restore Rollover Modal */}
      {showRestoreModal && (
        <RestoreRolloverModal
          restorePoints={restorePoints}
          onRestore={handleRestoreBudget}
          onClose={() => setShowRestoreModal(false)}
          loading={loading}
        />
      )}


      {/* Add/Edit Transaction Modal */}
      {showAddTransaction && (
        <TransactionModal
          form={transactionForm}
          setForm={setTransactionForm}
          budgetItems={budgetItems}
          onSubmit={editingTransaction ? handleUpdateTransaction : handleCreateTransaction}
          onClose={() => {
            setShowAddTransaction(false);
            setEditingTransaction(null);
          }}
          loading={loading}
          isEditing={!!editingTransaction}
          isBoard={isBoard}
        />
      )}

      {/* Add Balance Sheet Item Modal */}
      {showAddBalanceItem && (
        <BalanceSheetItemModal
          form={balanceItemForm}
          setForm={setBalanceItemForm}
          onSubmit={handleCreateBalanceItem}
          onClose={() => setShowAddBalanceItem(false)}
          loading={loading}
        />
      )}
    </div>
  );
};

// Budget Overview Component
const BudgetOverview = ({ summary, config, isBoard }) => {
  const [showConfigEdit, setShowConfigEdit] = useState(false);
  const [editedConfig, setEditedConfig] = useState({
    total_units: 0,
    notes: '',
    period_override: false,
    period_months: 12
  });

  useEffect(() => {
    if (config) {
      setEditedConfig({
        total_units: config.total_units || 0,
        notes: config.notes || '',
        period_override: config.period_override || false,
        period_months: config.period_months || 12
      });
    }
  }, [config]);

  const handleSaveConfig = async () => {
    try {
      const response = await api.put('/api/budget/config/units', {
        total_units: parseInt(editedConfig.total_units),
        notes: editedConfig.notes,
        period_override: editedConfig.period_override,
        period_months: parseInt(editedConfig.period_months)
      });
      
      if (response.data.success) {
        alert('Configuration updated successfully!');
        setShowConfigEdit(false);
        window.location.reload(); // Reload to get updated config
      }
    } catch (error) {
      console.error('Error updating config:', error);
      alert('Failed to update configuration: ' + (error.response?.data?.detail || error.message));
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  if (!summary) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <p className="text-gray-500">No budget data available. Create some budget items first.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm">💰</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Income</dt>
                  <dd className="text-lg font-medium text-gray-900">{formatCurrency(summary.total_income_actual)}</dd>
                  <dd className="text-xs text-gray-500">Budget: {formatCurrency(summary.total_income_budgeted)}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm">💸</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Expenses</dt>
                  <dd className="text-lg font-medium text-gray-900">{formatCurrency(summary.total_expenses_actual)}</dd>
                  <dd className="text-xs text-gray-500">Budget: {formatCurrency(summary.total_expenses_budgeted)}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className={`w-8 h-8 ${summary.net_actual >= 0 ? 'bg-green-500' : 'bg-red-500'} rounded-full flex items-center justify-center`}>
                  <span className="text-white text-sm">{summary.net_actual >= 0 ? '📈' : '📉'}</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Net Income</dt>
                  <dd className={`text-lg font-medium ${summary.net_actual >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(summary.net_actual)}
                  </dd>
                  <dd className="text-xs text-gray-500">Budget: {formatCurrency(summary.net_budgeted)}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className={`w-8 h-8 ${summary.variance >= 0 ? 'bg-blue-500' : 'bg-orange-500'} rounded-full flex items-center justify-center`}>
                  <span className="text-white text-sm">{summary.variance >= 0 ? '📊' : '⚠️'}</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Variance</dt>
                  <dd className={`text-lg font-medium ${summary.variance >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>
                    {formatCurrency(summary.variance)}
                  </dd>
                  <dd className="text-xs text-gray-500">vs Budget</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Budget Configuration */}
      {config && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">Budget Configuration</h3>
            {isBoard && (
              <button
                onClick={() => setShowConfigEdit(true)}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md text-sm font-medium"
              >
                ✏️ Edit Configuration
              </button>
            )}
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">Budget Year</label>
                <p className="mt-1 text-sm text-gray-900">{config.budget_year}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Total Properties</label>
                <p className="mt-1 text-sm text-gray-900">{config.total_units} units</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {config.assessment_frequency === 'yearly' ? 'Dues per Unit (Yearly)' : 'Dues per Unit (Monthly)'}
                </label>
                <p className="mt-1 text-sm text-gray-900">
                  {formatCurrency(config.assessment_frequency === 'yearly' ? config.dues_per_unit_yearly : config.dues_per_unit_monthly)}
                </p>
              </div>
            </div>
            {config.notes && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700">Notes</label>
                <p className="mt-1 text-sm text-gray-900">{config.notes}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Category Breakdown */}
      {summary.categories && Object.keys(summary.categories).length > 0 && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Category Breakdown</h3>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {Object.entries(summary.categories).map(([category, data]) => (
                <div key={category} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <h4 className="font-medium text-gray-900 capitalize">{category.replace('_', ' ')}</h4>
                    <p className="text-sm text-gray-600">
                      Actual: {formatCurrency(data.actual)} | Budget: {formatCurrency(data.budgeted)}
                    </p>
                  </div>
                  <div className={`text-right ${data.variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    <div className="font-medium">{formatCurrency(data.variance)}</div>
                    <div className="text-xs">Variance</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Edit Configuration Modal */}
      {showConfigEdit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6">
            <h3 className="text-lg font-semibold mb-4">Edit Budget Configuration</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Total Properties <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  min="1"
                  value={editedConfig.total_units}
                  onChange={(e) => setEditedConfig({...editedConfig, total_units: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Enter total number of properties"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Update if the number of properties in your HOA has changed
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes (Optional)
                </label>
                <textarea
                  value={editedConfig.notes}
                  onChange={(e) => setEditedConfig({...editedConfig, notes: e.target.value})}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Add any notes about the budget configuration..."
                />
              </div>

              {/* Budget Period Configuration */}
              <div className="border-t pt-4">
                <h4 className="text-md font-medium text-gray-900 mb-3">Budget Period Configuration</h4>
                <p className="text-sm text-gray-600 mb-3">
                  Budgets are based on 1 year by default. If you want to adjust the formulas for a different period, enable the override below.
                </p>
                
                <div className="flex items-center mb-3">
                  <input
                    type="checkbox"
                    id="period_override"
                    checked={editedConfig.period_override || false}
                    onChange={(e) => setEditedConfig({
                      ...editedConfig, 
                      period_override: e.target.checked,
                      period_months: e.target.checked ? (editedConfig.period_months || 12) : 12
                    })}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <label htmlFor="period_override" className="ml-2 block text-sm text-gray-900">
                    Override default yearly period
                  </label>
                </div>

                {editedConfig.period_override && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Number of Months (1-24)
                    </label>
                    <select
                      value={editedConfig.period_months || 12}
                      onChange={(e) => setEditedConfig({...editedConfig, period_months: parseInt(e.target.value)})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      {Array.from({length: 24}, (_, i) => i + 1).map(month => (
                        <option key={month} value={month}>
                          {month} month{month !== 1 ? 's' : ''}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      This will calculate yearly/monthly amounts based on {editedConfig.period_months || 12} months instead of 12.
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowConfigEdit(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveConfig}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Budget Items View Component
const BudgetItemsView = ({ items, isBoard, onAddItem, onEditItem, onRefresh, config, setShowSetBudgetModal, setShowRolloverModal, onOpenRestoreModal, onSetBudget, onRolloverBudget, onSyncActuals, setBudgetForm, rolloverAction, loading, setLoading, showProposedColumn, setShowProposedColumn }) => {
  const [viewMode, setViewMode] = useState('monthly'); // monthly or yearly
  const [monthFilter, setMonthFilter] = useState('all'); // all, or specific month like '2025-01'
  const [editingPriorYear, setEditingPriorYear] = useState({}); // Track which cells are being edited
  const [priorYearValues, setPriorYearValues] = useState({}); // Track edited values
  const [editingProposed, setEditingProposed] = useState({}); // Track proposed edits
  const [proposedValues, setProposedValues] = useState({}); // Track proposed values
  const [editingBeginningBalance, setEditingBeginningBalance] = useState(false); // Track beginning balance edit
  const [beginningBalanceInput, setBeginningBalanceInput] = useState(''); // Input value for beginning balance
  const [beginningBalanceDate, setBeginningBalanceDate] = useState(''); // Date for beginning balance
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  // Helper function to adjust amounts based on view mode
  const adjustAmountForView = (amount, recurrence) => {
    if (viewMode === 'yearly') {
      if (recurrence === 'monthly') return amount * 12;
      if (recurrence === 'quarterly') return amount * 4;
      return amount;
    } else { // monthly view
      if (recurrence === 'yearly') return amount / 12;
      if (recurrence === 'quarterly') return amount / 3;
      return amount;
    }
  };

  // Helper function to get display recurrence
  const getDisplayRecurrence = (recurrence) => {
    if (viewMode === 'yearly') return 'yearly';
    return 'monthly';
  };

  // Auto-calculate dues based on config
  const getAutoDuesAmount = () => {
    if (!config) return 0;
    
    // Check if we have yearly or monthly dues
    const frequency = config.assessment_frequency || 'monthly';
    
    if (frequency === 'yearly') {
      // For yearly dues: total_units × yearly_amount
      const yearlyDues = config.total_units * (config.dues_per_unit_yearly || 0);
      // If viewing monthly, divide by 12
      return viewMode === 'monthly' ? yearlyDues / 12 : yearlyDues;
    } else {
      // For monthly/quarterly dues: total_units × monthly_amount
      const monthlyDues = config.total_units * (config.dues_per_unit_monthly || 0);
      // If viewing yearly, multiply by 12
      return viewMode === 'yearly' ? monthlyDues * 12 : monthlyDues;
    }
  };

  const handleEditItem = (item) => {
    onEditItem(item);
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this budget item?')) {
      return;
    }
    
    try {
      const response = await api.delete(`/api/budget/items/${itemId}`);
      if (response.data.success) {
        alert('Budget item deleted successfully!');
        onRefresh();
      }
    } catch (error) {
      console.error('Error deleting budget item:', error);
      alert('Failed to delete budget item: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleUpdatePriorYear = async (itemId, field, value) => {
    try {
      const updateData = {
        [field]: parseFloat(value) || 0
      };
      
      const response = await api.put(`/api/budget/items/${itemId}`, updateData);
      
      if (response.data.success) {
        // Clear editing state
        setEditingPriorYear(prev => ({...prev, [`${itemId}-${field}`]: false}));
        setPriorYearValues(prev => ({...prev, [`${itemId}-${field}`]: ''}));
        
        // Refresh data
        onRefresh();
      }
    } catch (error) {
      console.error('Error updating prior year value:', error);
      alert('Failed to update prior year value: ' + (error.response?.data?.detail || error.message));
    }
  };

  const startEditingPriorYear = (itemId, field, currentValue) => {
    setEditingPriorYear(prev => ({...prev, [`${itemId}-${field}`]: true}));
    setPriorYearValues(prev => ({...prev, [`${itemId}-${field}`]: currentValue || 0}));
  };

  const cancelEditingPriorYear = (itemId, field) => {
    setEditingPriorYear(prev => ({...prev, [`${itemId}-${field}`]: false}));
    setPriorYearValues(prev => ({...prev, [`${itemId}-${field}`]: ''}));
  };

  // Helper to get budgeted amount - respects auto_calc flag for dues
  const getBudgetedAmount = (item) => {
    // Only auto-calculate for dues items when auto_calc is true (or not set)
    if (item.subcategory === 'dues' && item.auto_calc !== false) {
      return getAutoDuesAmount();
    }
    return adjustAmountForView(item.budgeted_amount, item.recurrence);
  };

  // Handle proposed amount update
  const handleUpdateProposed = async (itemId, value) => {
    try {
      const response = await api.put(`/api/budget/items/${itemId}`, {
        proposed_amount: parseFloat(value) || 0
      });
      if (response.data?.success) {
        setEditingProposed(prev => ({...prev, [itemId]: false}));
        onRefresh();
      } else {
        throw new Error(response.data?.message || 'Update failed');
      }
    } catch (error) {
      console.error('Error updating proposed amount:', error);
      const errMsg = typeof error === 'string' ? error : 
                     error?.response?.data?.detail || 
                     error?.response?.data?.message ||
                     error?.message || 
                     'Unknown error';
      alert('Failed to update proposed amount: ' + errMsg);
    }
  };

  // Handle beginning balance update
  const handleUpdateBeginningBalance = async () => {
    try {
      setLoading(true);
      // Use the selected date or default to today
      const asOfDate = beginningBalanceDate || new Date().toISOString().split('T')[0];
      const response = await api.put('/api/budget/config/beginning-balance', {
        beginning_balance: parseFloat(beginningBalanceInput) || 0,
        as_of_date: asOfDate + 'T00:00:00'
      });
      if (response.data?.success) {
        setEditingBeginningBalance(false);
        setBeginningBalanceInput('');
        setBeginningBalanceDate('');
        alert(`Beginning balance set to $${parseFloat(beginningBalanceInput).toLocaleString()} as of ${asOfDate}`);
        onRefresh();
      } else {
        throw new Error(response.data?.message || 'Update failed');
      }
    } catch (error) {
      console.error('Error updating beginning balance:', error);
      const errMsg = error?.response?.data?.detail || error?.message || 'Unknown error';
      alert('Failed to update beginning balance: ' + errMsg);
    } finally {
      setLoading(false);
    }
  };


  const incomeItems = items.filter(item => item.category === 'income');
  const expenseItems = items.filter(item => item.category === 'expense');

  // Calculate totals
  const totalIncomeBudgeted = incomeItems.reduce((sum, item) => {
    return sum + getBudgetedAmount(item);
  }, 0);

  const totalIncomeActual = incomeItems.reduce((sum, item) => {
    return sum + (item.actual_amount || 0);  // Raw total, not adjusted
  }, 0);

  const totalExpensesBudgeted = expenseItems.reduce((sum, item) => {
    return sum + adjustAmountForView(item.budgeted_amount, item.recurrence);
  }, 0);

  const totalExpensesActual = expenseItems.reduce((sum, item) => {
    return sum + (item.actual_amount || 0);  // Raw total, not adjusted
  }, 0);

  // Cash calculations - get beginning balance from budget config (NOT HARDCODED)
  const cashBeginningOfYear = config?.beginning_balance_reserve || config?.beginning_balance || 0;
  const currentCashBalance = cashBeginningOfYear + totalIncomeActual - totalExpensesActual;
  const projectedCashBalance = cashBeginningOfYear + totalIncomeBudgeted - totalExpensesBudgeted;

  return (
    <div className="space-y-6">
      {/* Configuration Note */}
      {config && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">Budget Configuration</h3>
              <div className="mt-1 text-sm text-blue-700">
                <p>
                  Budgets are based on <strong>{config.period_override ? `${config.period_months} month${config.period_months !== 1 ? 's' : ''}` : '1 year'}</strong>.
                  {config.period_override && (
                    <span> Monthly amounts are calculated based on this {config.period_months}-month period.</span>
                  )}
                  {!config.period_override && (
                    <span> If you want to adjust the formulas for a different period, you can change this in the configuration settings.</span>
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Mode Toggle, Filters and Actions */}
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-gray-700">View:</span>
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('monthly')}
                className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                  viewMode === 'monthly'
                    ? 'bg-white text-indigo-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setViewMode('yearly')}
                className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                  viewMode === 'yearly'
                    ? 'bg-white text-indigo-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                Yearly
              </button>
            </div>
          </div>

          {/* Month Filter */}
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-gray-700">Month:</span>
            <select
              value={monthFilter}
              onChange={(e) => setMonthFilter(e.target.value)}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="all">All Months</option>
              <option value="2025-01">January 2025</option>
              <option value="2025-02">February 2025</option>
              <option value="2025-03">March 2025</option>
              <option value="2025-04">April 2025</option>
              <option value="2025-05">May 2025</option>
              <option value="2025-06">June 2025</option>
              <option value="2025-07">July 2025</option>
              <option value="2025-08">August 2025</option>
              <option value="2025-09">September 2025</option>
              <option value="2025-10">October 2025</option>
              <option value="2025-11">November 2025</option>
              <option value="2025-12">December 2025</option>
            </select>
          </div>
          
          {/* Auto-calculation indicator */}
          {config && (
            <div className="text-sm text-blue-600 bg-blue-50 px-3 py-1 rounded-md">
              💡 Dues auto-calculated: {config.total_units} units × {
                config.assessment_frequency === 'yearly' 
                  ? `${formatCurrency(config.dues_per_unit_yearly || 0)}/year`
                  : `${formatCurrency(config.dues_per_unit_monthly || 0)}/month`
              } = {formatCurrency(getAutoDuesAmount())} ({viewMode === 'yearly' ? 'annual total' : 'monthly total'})
            </div>
          )}
        </div>
        
        {isBoard && (
          <div className="flex flex-wrap gap-2 mt-2 sm:mt-0">
            <button
              onClick={onAddItem}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded-md text-xs sm:text-sm font-medium whitespace-nowrap"
            >
              + Add Item
            </button>
            <button
              onClick={() => setShowSetBudgetModal(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-md text-xs sm:text-sm font-medium whitespace-nowrap"
            >
              📊 Set Budget
            </button>
            <button
              onClick={() => setShowRolloverModal(true)}
              className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-2 rounded-md text-xs sm:text-sm font-medium whitespace-nowrap"
            >
              🔄 Rollover
            </button>
            <button
              onClick={onOpenRestoreModal}
              className="bg-amber-600 hover:bg-amber-700 text-white px-3 py-2 rounded-md text-xs sm:text-sm font-medium whitespace-nowrap"
              title="Restore from a previous rollover save point"
              data-testid="restore-rollover-btn"
            >
              ↩️ Restore
            </button>
            <button
              onClick={onSyncActuals}
              className="bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-md text-xs sm:text-sm font-medium whitespace-nowrap"
              title="Recalculate actuals from transactions"
            >
              🔄 Sync
            </button>
            <button
              onClick={() => setShowProposedColumn(!showProposedColumn)}
              className={`${showProposedColumn ? 'bg-orange-600 hover:bg-orange-700' : 'bg-gray-500 hover:bg-gray-600'} text-white px-4 py-2 rounded-md text-sm font-medium`}
              title="Toggle Proposed Budget column"
            >
              {showProposedColumn ? '📋 Hide Proposed' : '📋 Show Proposed'}
            </button>
          </div>
        )}
      </div>

      {/* Income Items */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200 bg-green-50">
          <h3 className="text-lg font-medium text-green-900">Income Items</h3>
        </div>
        <div className="p-6">
          {incomeItems.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No income items configured</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Budgeted ({viewMode})</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actual (Total)</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-blue-600 uppercase tracking-wider bg-blue-50">Calc Actual ({viewMode})</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Variance</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-purple-600 uppercase tracking-wider bg-purple-50">Prior Yr Budget</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-purple-600 uppercase tracking-wider bg-purple-50">Prior Yr Actual</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Original Frequency</th>
                    {showProposedColumn && <th className="px-6 py-3 text-left text-xs font-medium text-orange-600 uppercase tracking-wider bg-orange-50">Proposed</th>}
                    {isBoard && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {incomeItems.map((item) => {
                    const adjustedBudgeted = getBudgetedAmount(item);
                    const adjustedActual = adjustAmountForView(item.actual_amount, item.recurrence);
                    const variance = adjustedActual - adjustedBudgeted;
                    
                    return (
                      <tr key={item.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {item.description}
                          {item.subcategory === 'dues' && item.auto_calc !== false && (
                            <div className="text-xs text-blue-600">Auto-calculated</div>
                          )}
                          {item.subcategory === 'dues' && item.auto_calc === false && (
                            <div className="text-xs text-gray-500">Manual entry</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">{item.subcategory.replace('_', ' ')}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(adjustedBudgeted)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(item.actual_amount)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600 bg-blue-50">{formatCurrency(adjustedActual)}</td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm ${variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {formatCurrency(variance)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-purple-600 bg-purple-50">
                          {isBoard && editingPriorYear[`${item.id}-prior_year_budget`] ? (
                            <div className="flex items-center space-x-1">
                              <input
                                type="number"
                                step="0.01"
                                value={priorYearValues[`${item.id}-prior_year_budget`]}
                                onChange={(e) => setPriorYearValues(prev => ({...prev, [`${item.id}-prior_year_budget`]: e.target.value}))}
                                className="w-24 px-2 py-1 border border-purple-300 rounded text-sm"
                                autoFocus
                              />
                              <button
                                onClick={() => handleUpdatePriorYear(item.id, 'prior_year_budget', priorYearValues[`${item.id}-prior_year_budget`])}
                                className="text-green-600 hover:text-green-800 text-xs"
                              >
                                ✓
                              </button>
                              <button
                                onClick={() => cancelEditingPriorYear(item.id, 'prior_year_budget')}
                                className="text-red-600 hover:text-red-800 text-xs"
                              >
                                ✕
                              </button>
                            </div>
                          ) : (
                            <div 
                              className={isBoard ? "cursor-pointer hover:bg-purple-100 p-1 rounded" : ""}
                              onClick={() => isBoard && startEditingPriorYear(item.id, 'prior_year_budget', item.prior_year_budget)}
                              title={isBoard ? "Click to edit" : ""}
                            >
                              {formatCurrency(item.prior_year_budget || 0)}
                              {isBoard && <span className="text-xs ml-1">✎</span>}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-purple-600 bg-purple-50">
                          {isBoard && editingPriorYear[`${item.id}-prior_year_actual`] ? (
                            <div className="flex items-center space-x-1">
                              <input
                                type="number"
                                step="0.01"
                                value={priorYearValues[`${item.id}-prior_year_actual`]}
                                onChange={(e) => setPriorYearValues(prev => ({...prev, [`${item.id}-prior_year_actual`]: e.target.value}))}
                                className="w-24 px-2 py-1 border border-purple-300 rounded text-sm"
                                autoFocus
                              />
                              <button
                                onClick={() => handleUpdatePriorYear(item.id, 'prior_year_actual', priorYearValues[`${item.id}-prior_year_actual`])}
                                className="text-green-600 hover:text-green-800 text-xs"
                              >
                                ✓
                              </button>
                              <button
                                onClick={() => cancelEditingPriorYear(item.id, 'prior_year_actual')}
                                className="text-red-600 hover:text-red-800 text-xs"
                              >
                                ✕
                              </button>
                            </div>
                          ) : (
                            <div 
                              className={isBoard ? "cursor-pointer hover:bg-purple-100 p-1 rounded" : ""}
                              onClick={() => isBoard && startEditingPriorYear(item.id, 'prior_year_actual', item.prior_year_actual)}
                              title={isBoard ? "Click to edit" : ""}
                            >
                              {formatCurrency(item.prior_year_actual || 0)}
                              {isBoard && <span className="text-xs ml-1">✎</span>}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">{item.recurrence}</td>
                        {showProposedColumn && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-orange-600 bg-orange-50">
                            {isBoard && editingProposed[item.id] ? (
                              <div className="flex items-center space-x-1">
                                <input
                                  type="number"
                                  step="0.01"
                                  value={proposedValues[item.id] ?? item.proposed_amount ?? ''}
                                  onChange={(e) => setProposedValues(prev => ({...prev, [item.id]: e.target.value}))}
                                  className="w-24 px-2 py-1 border border-orange-300 rounded text-sm"
                                  autoFocus
                                  placeholder={item.recurrence === 'monthly' ? 'Monthly' : item.recurrence === 'quarterly' ? 'Quarterly' : 'Yearly'}
                                />
                                <button onClick={() => handleUpdateProposed(item.id, proposedValues[item.id])} className="text-green-600 hover:text-green-800 text-xs">✓</button>
                                <button onClick={() => {setEditingProposed(prev => ({...prev, [item.id]: false})); setProposedValues(prev => ({...prev, [item.id]: ''}));}} className="text-red-600 hover:text-red-800 text-xs">✕</button>
                              </div>
                            ) : (
                              <div 
                                className={isBoard ? "cursor-pointer hover:bg-orange-100 p-1 rounded" : ""}
                                onClick={() => isBoard && (setEditingProposed(prev => ({...prev, [item.id]: true})), setProposedValues(prev => ({...prev, [item.id]: item.proposed_amount || ''})))}
                                title={isBoard ? "Click to edit proposed amount" : ""}
                              >
                                {/* Show the entered value and calculated yearly if monthly/quarterly */}
                                <div className="flex flex-col">
                                  <span className="font-medium">{formatCurrency(item.proposed_amount || 0)}</span>
                                  {item.proposed_amount > 0 && item.recurrence === 'monthly' && (
                                    <span className="text-xs text-orange-500">= {formatCurrency(item.proposed_amount * 12)}/yr</span>
                                  )}
                                  {item.proposed_amount > 0 && item.recurrence === 'quarterly' && (
                                    <span className="text-xs text-orange-500">= {formatCurrency(item.proposed_amount * 4)}/yr</span>
                                  )}
                                </div>
                                {isBoard && <span className="text-xs ml-1">✎</span>}
                              </div>
                            )}
                          </td>
                        )}
                        {isBoard && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleEditItem(item)}
                                className="text-indigo-600 hover:text-indigo-900 text-xs bg-indigo-50 hover:bg-indigo-100 px-2 py-1 rounded"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteItem(item.id)}
                                className="text-red-600 hover:text-red-900 text-xs bg-red-50 hover:bg-red-100 px-2 py-1 rounded"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Expense Items */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200 bg-red-50">
          <h3 className="text-lg font-medium text-red-900">Expense Items</h3>
        </div>
        <div className="p-6">
          {expenseItems.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No expense items configured</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Budgeted ({viewMode})</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actual (Total)</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-blue-600 uppercase tracking-wider bg-blue-50">Calc Actual ({viewMode})</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Variance</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-purple-600 uppercase tracking-wider bg-purple-50">Prior Yr Budget</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-purple-600 uppercase tracking-wider bg-purple-50">Prior Yr Actual</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Original Frequency</th>
                    {showProposedColumn && <th className="px-6 py-3 text-left text-xs font-medium text-orange-600 uppercase tracking-wider bg-orange-50">Proposed</th>}
                    {isBoard && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {expenseItems.map((item) => {
                    const adjustedBudgeted = adjustAmountForView(item.budgeted_amount, item.recurrence);
                    const adjustedActual = adjustAmountForView(item.actual_amount, item.recurrence);
                    const variance = adjustedBudgeted - adjustedActual; // For expenses, positive variance is good (under budget)
                    
                    return (
                      <tr key={item.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.description}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">{item.subcategory.replace('_', ' ')}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(adjustedBudgeted)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(item.actual_amount)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600 bg-blue-50">{formatCurrency(adjustedActual)}</td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm ${variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {formatCurrency(variance)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-purple-600 bg-purple-50">
                          {isBoard && editingPriorYear[`${item.id}-prior_year_budget`] ? (
                            <div className="flex items-center space-x-1">
                              <input
                                type="number"
                                step="0.01"
                                value={priorYearValues[`${item.id}-prior_year_budget`]}
                                onChange={(e) => setPriorYearValues(prev => ({...prev, [`${item.id}-prior_year_budget`]: e.target.value}))}
                                className="w-24 px-2 py-1 border border-purple-300 rounded text-sm"
                                autoFocus
                              />
                              <button
                                onClick={() => handleUpdatePriorYear(item.id, 'prior_year_budget', priorYearValues[`${item.id}-prior_year_budget`])}
                                className="text-green-600 hover:text-green-800 text-xs"
                              >
                                ✓
                              </button>
                              <button
                                onClick={() => cancelEditingPriorYear(item.id, 'prior_year_budget')}
                                className="text-red-600 hover:text-red-800 text-xs"
                              >
                                ✕
                              </button>
                            </div>
                          ) : (
                            <div 
                              className={isBoard ? "cursor-pointer hover:bg-purple-100 p-1 rounded" : ""}
                              onClick={() => isBoard && startEditingPriorYear(item.id, 'prior_year_budget', item.prior_year_budget)}
                              title={isBoard ? "Click to edit" : ""}
                            >
                              {formatCurrency(item.prior_year_budget || 0)}
                              {isBoard && <span className="text-xs ml-1">✎</span>}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-purple-600 bg-purple-50">
                          {isBoard && editingPriorYear[`${item.id}-prior_year_actual`] ? (
                            <div className="flex items-center space-x-1">
                              <input
                                type="number"
                                step="0.01"
                                value={priorYearValues[`${item.id}-prior_year_actual`]}
                                onChange={(e) => setPriorYearValues(prev => ({...prev, [`${item.id}-prior_year_actual`]: e.target.value}))}
                                className="w-24 px-2 py-1 border border-purple-300 rounded text-sm"
                                autoFocus
                              />
                              <button
                                onClick={() => handleUpdatePriorYear(item.id, 'prior_year_actual', priorYearValues[`${item.id}-prior_year_actual`])}
                                className="text-green-600 hover:text-green-800 text-xs"
                              >
                                ✓
                              </button>
                              <button
                                onClick={() => cancelEditingPriorYear(item.id, 'prior_year_actual')}
                                className="text-red-600 hover:text-red-800 text-xs"
                              >
                                ✕
                              </button>
                            </div>
                          ) : (
                            <div 
                              className={isBoard ? "cursor-pointer hover:bg-purple-100 p-1 rounded" : ""}
                              onClick={() => isBoard && startEditingPriorYear(item.id, 'prior_year_actual', item.prior_year_actual)}
                              title={isBoard ? "Click to edit" : ""}
                            >
                              {formatCurrency(item.prior_year_actual || 0)}
                              {isBoard && <span className="text-xs ml-1">✎</span>}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">{item.recurrence}</td>
                        {showProposedColumn && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-orange-600 bg-orange-50">
                            {isBoard && editingProposed[item.id] ? (
                              <div className="flex items-center space-x-1">
                                <input
                                  type="number"
                                  step="0.01"
                                  value={proposedValues[item.id] ?? item.proposed_amount ?? ''}
                                  onChange={(e) => setProposedValues(prev => ({...prev, [item.id]: e.target.value}))}
                                  className="w-24 px-2 py-1 border border-orange-300 rounded text-sm"
                                  autoFocus
                                  placeholder={item.recurrence === 'monthly' ? 'Monthly' : item.recurrence === 'quarterly' ? 'Quarterly' : 'Yearly'}
                                />
                                <button onClick={() => handleUpdateProposed(item.id, proposedValues[item.id])} className="text-green-600 hover:text-green-800 text-xs">✓</button>
                                <button onClick={() => {setEditingProposed(prev => ({...prev, [item.id]: false})); setProposedValues(prev => ({...prev, [item.id]: ''}));}} className="text-red-600 hover:text-red-800 text-xs">✕</button>
                              </div>
                            ) : (
                              <div 
                                className={isBoard ? "cursor-pointer hover:bg-orange-100 p-1 rounded" : ""}
                                onClick={() => isBoard && (setEditingProposed(prev => ({...prev, [item.id]: true})), setProposedValues(prev => ({...prev, [item.id]: item.proposed_amount || ''})))}
                                title={isBoard ? "Click to edit proposed amount" : ""}
                              >
                                <div className="flex flex-col">
                                  <span className="font-medium">{formatCurrency(item.proposed_amount || 0)}</span>
                                  {item.proposed_amount > 0 && item.recurrence === 'monthly' && (
                                    <span className="text-xs text-orange-500">= {formatCurrency(item.proposed_amount * 12)}/yr</span>
                                  )}
                                  {item.proposed_amount > 0 && item.recurrence === 'quarterly' && (
                                    <span className="text-xs text-orange-500">= {formatCurrency(item.proposed_amount * 4)}/yr</span>
                                  )}
                                </div>
                                {isBoard && <span className="text-xs ml-1">✎</span>}
                              </div>
                            )}
                          </td>
                        )}
                        {isBoard && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleEditItem(item)}
                                className="text-indigo-600 hover:text-indigo-900 text-xs bg-indigo-50 hover:bg-indigo-100 px-2 py-1 rounded"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteItem(item.id)}
                                className="text-red-600 hover:text-red-900 text-xs bg-red-50 hover:bg-red-100 px-2 py-1 rounded"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* TOTALS SUMMARY SECTION */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Total Income */}
        <div className="bg-green-50 shadow rounded-lg border border-green-200">
          <div className="px-6 py-4 border-b border-green-200 bg-green-100">
            <h3 className="text-lg font-medium text-green-900">📈 Total Income ({viewMode})</h3>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">Budgeted</span>
                <span className="text-xl font-bold text-green-600">{formatCurrency(totalIncomeBudgeted)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">Actual</span>
                <span className="text-xl font-bold text-green-700">{formatCurrency(totalIncomeActual)}</span>
              </div>
              <div className="border-t border-green-200 pt-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">Variance</span>
                  <span className={`text-lg font-bold ${(totalIncomeActual - totalIncomeBudgeted) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(totalIncomeActual - totalIncomeBudgeted)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Total Expenses */}
        <div className="bg-red-50 shadow rounded-lg border border-red-200">
          <div className="px-6 py-4 border-b border-red-200 bg-red-100">
            <h3 className="text-lg font-medium text-red-900">📉 Total Expenses ({viewMode})</h3>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">Budgeted</span>
                <span className="text-xl font-bold text-red-600">{formatCurrency(totalExpensesBudgeted)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">Actual</span>
                <span className="text-xl font-bold text-red-700">{formatCurrency(totalExpensesActual)}</span>
              </div>
              <div className="border-t border-red-200 pt-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">Variance</span>
                  <span className={`text-lg font-bold ${(totalExpensesBudgeted - totalExpensesActual) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(totalExpensesBudgeted - totalExpensesActual)}
                  </span>
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {(totalExpensesBudgeted - totalExpensesActual) >= 0 ? 'Under budget' : 'Over budget'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Total Cash */}
        <div className="bg-blue-50 shadow rounded-lg border border-blue-200">
          <div className="px-6 py-4 border-b border-blue-200 bg-blue-100">
            <h3 className="text-lg font-medium text-blue-900">💰 Cash Balance ({viewMode})</h3>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">Beginning of Year (Cash Balance)</span>
                {isBoard && editingBeginningBalance ? (
                  <div className="flex flex-col space-y-2">
                    <div className="flex items-center space-x-2">
                      <span className="text-gray-500">$</span>
                      <input
                        type="number"
                        step="0.01"
                        value={beginningBalanceInput}
                        onChange={(e) => setBeginningBalanceInput(e.target.value)}
                        className="w-32 px-2 py-1 border border-blue-300 rounded text-sm"
                        placeholder="Amount"
                        autoFocus
                      />
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-gray-500">As of:</span>
                      <input
                        type="date"
                        value={beginningBalanceDate}
                        onChange={(e) => setBeginningBalanceDate(e.target.value)}
                        className="w-36 px-2 py-1 border border-blue-300 rounded text-sm"
                      />
                    </div>
                    <div className="flex space-x-2">
                      <button onClick={handleUpdateBeginningBalance} disabled={loading} className="text-green-600 hover:text-green-800 text-sm font-medium">✓ Save</button>
                      <button onClick={() => {setEditingBeginningBalance(false); setBeginningBalanceInput(''); setBeginningBalanceDate('');}} className="text-red-600 hover:text-red-800 text-sm">✕ Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div 
                    className={isBoard ? "cursor-pointer hover:bg-blue-100 px-2 py-1 rounded flex items-center space-x-1" : ""}
                    onClick={() => isBoard && (setEditingBeginningBalance(true), setBeginningBalanceInput(cashBeginningOfYear.toString()), setBeginningBalanceDate(''))}
                    title={isBoard ? "Click to edit beginning balance and date" : ""}
                  >
                    <span className="text-xl font-bold text-blue-600">{formatCurrency(cashBeginningOfYear)}</span>
                    {isBoard && <span className="text-xs text-blue-400">✎</span>}
                  </div>
                )}
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">Current Balance</span>
                <span className={`text-xl font-bold ${currentCashBalance >= 0 ? 'text-blue-700' : 'text-red-600'}`}>
                  {formatCurrency(currentCashBalance)}
                </span>
              </div>
              <div className="border-t border-blue-200 pt-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">Projected Balance</span>
                  <span className={`text-lg font-bold ${projectedCashBalance >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                    {formatCurrency(projectedCashBalance)}
                  </span>
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  Based on budget projections
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Transactions View Component
const TransactionsView = ({ transactions, budgetItems, isBoard, user, onAddTransaction, onEditTransaction, onDeleteTransaction, onRefresh }) => {
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear().toString());
  const [monthFilter, setMonthFilter] = useState('all'); // all, or specific month like '01'
  const [typeFilter, setTypeFilter] = useState('all'); // all, income, expense
  const [searchTerm, setSearchTerm] = useState('');

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  // Get unique years from transactions - parse from string to avoid timezone shifts
  const availableYears = [...new Set(transactions.map(t => {
    const dateStr = t.transaction_date;
    if (typeof dateStr === 'string') {
      const match = dateStr.match(/^(\d{4})/);
      if (match) return parseInt(match[1]);
    }
    return new Date(dateStr).getFullYear();
  }))].sort((a, b) => b - a);

  // Add current year if not in list
  const currentYear = new Date().getFullYear();
  if (!availableYears.includes(currentYear)) {
    availableYears.unshift(currentYear);
  }

  // Filter transactions by year, month, type, and search
  const filteredTransactions = transactions.filter(transaction => {
    // Parse date WITHOUT timezone conversion - use the date as stored
    const dateStr = transaction.transaction_date;
    let transactionYear, transactionMonth;
    
    if (typeof dateStr === 'string') {
      // Extract year and month directly from ISO string to avoid timezone shifts
      // Format could be "2025-01-15T00:00:00Z" or "2025-01-15"
      const match = dateStr.match(/^(\d{4})-(\d{2})/);
      if (match) {
        transactionYear = match[1];
        transactionMonth = match[2];
      } else {
        // Fallback to Date parsing
        const d = new Date(dateStr);
        transactionYear = d.getFullYear().toString();
        transactionMonth = (d.getMonth() + 1).toString().padStart(2, '0');
      }
    } else if (dateStr instanceof Date) {
      transactionYear = dateStr.getFullYear().toString();
      transactionMonth = (dateStr.getMonth() + 1).toString().padStart(2, '0');
    } else {
      // Can't parse, include by default
      transactionYear = yearFilter;
      transactionMonth = monthFilter;
    }
    
    // Year filter
    if (yearFilter !== 'all' && transactionYear !== yearFilter) return false;
    
    // Month filter
    if (monthFilter !== 'all' && transactionMonth !== monthFilter) return false;
    
    // Type filter (income/expense)
    if (typeFilter === 'income' && transaction.category !== 'income') return false;
    if (typeFilter === 'expense' && transaction.category === 'income') return false;
    
    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchesSearch = 
        transaction.description?.toLowerCase().includes(search) ||
        transaction.vendor?.toLowerCase().includes(search) ||
        transaction.subcategory?.toLowerCase().includes(search) ||
        transaction.property_address?.toLowerCase().includes(search) ||
        transaction.property_identifier?.toLowerCase().includes(search) ||
        transaction.reference_number?.toLowerCase().includes(search);
      if (!matchesSearch) return false;
    }
    
    return true;
  });

  // Calculate totals for filtered transactions
  const totalIncome = filteredTransactions.filter(t => t.category === 'income').reduce((sum, t) => sum + Math.abs(t.amount || 0), 0);
  const totalExpenses = filteredTransactions.filter(t => t.category !== 'income').reduce((sum, t) => sum + Math.abs(t.amount || 0), 0);

  return (
    <div className="space-y-6">
      {/* Filters Row */}
      <div className="bg-white shadow rounded-lg p-4">
        <div className="flex flex-wrap gap-4 items-center">
          {/* Year Filter */}
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-gray-700">Year:</span>
            <select
              value={yearFilter}
              onChange={(e) => setYearFilter(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="all">All Years</option>
              {availableYears.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>

          {/* Month Filter */}
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-gray-700">Month:</span>
            <select
              value={monthFilter}
              onChange={(e) => setMonthFilter(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="all">All Months</option>
              <option value="01">January</option>
              <option value="02">February</option>
              <option value="03">March</option>
              <option value="04">April</option>
              <option value="05">May</option>
              <option value="06">June</option>
              <option value="07">July</option>
              <option value="08">August</option>
              <option value="09">September</option>
              <option value="10">October</option>
              <option value="11">November</option>
              <option value="12">December</option>
            </select>
          </div>

          {/* Type Filter */}
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-gray-700">Type:</span>
            <div className="flex rounded-md shadow-sm">
              <button
                onClick={() => setTypeFilter('all')}
                className={`px-3 py-1.5 text-sm font-medium rounded-l-md border ${
                  typeFilter === 'all' 
                    ? 'bg-indigo-600 text-white border-indigo-600' 
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setTypeFilter('income')}
                className={`px-3 py-1.5 text-sm font-medium border-t border-b ${
                  typeFilter === 'income' 
                    ? 'bg-green-600 text-white border-green-600' 
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                Income
              </button>
              <button
                onClick={() => setTypeFilter('expense')}
                className={`px-3 py-1.5 text-sm font-medium rounded-r-md border ${
                  typeFilter === 'expense' 
                    ? 'bg-red-600 text-white border-red-600' 
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                Expenses
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="flex-1 min-w-[200px]">
            <input
              type="text"
              placeholder="Search description, vendor, property..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          {/* Add Transaction Button */}
          {isBoard && (
            <div className="flex gap-2">
              <TransactionImportExport 
                user={user} 
                onRefresh={() => onRefresh()} 
              />
              <button
                onClick={onAddTransaction}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-1.5 rounded-md text-sm font-medium whitespace-nowrap"
              >
                Add Transaction
              </button>
            </div>
          )}
        </div>

        {/* Summary Stats */}
        <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-gray-200">
          <div className="text-sm text-gray-600">
            Showing <span className="font-medium">{filteredTransactions.length}</span> of {transactions.length} transactions
          </div>
          <div className="text-sm">
            <span className="text-green-600 font-medium">Income: {formatCurrency(totalIncome)}</span>
            <span className="mx-2">|</span>
            <span className="text-red-600 font-medium">Expenses: {formatCurrency(totalExpenses)}</span>
            <span className="mx-2">|</span>
            <span className={`font-medium ${totalIncome - totalExpenses >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              Net: {formatCurrency(totalIncome - totalExpenses)}
            </span>
          </div>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Transactions</h3>
        </div>
        <div className="p-6">
          {filteredTransactions.length === 0 ? (
            <p className="text-gray-500 text-center py-4">
              No transactions found matching your filters
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vendor</th>
                    {isBoard && (
                      <>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Property ID</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Property Address</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Linked Budget Item</th>
                      </>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reference</th>
                    {isBoard && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredTransactions.map((transaction) => {
                    // Find linked budget item name
                    const linkedBudgetItem = budgetItems?.find(item => item.id === transaction.budget_line_item_id);
                    const linkedItemName = linkedBudgetItem ? linkedBudgetItem.description : null;
                    
                    return (
                    <tr key={transaction.id} className={!transaction.budget_line_item_id && isBoard ? 'bg-yellow-50' : ''}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatDate(transaction.transaction_date)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{transaction.description}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">{transaction.subcategory ? transaction.subcategory.replace('_', ' ') : 'N/A'}</td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${transaction.category === 'income' ? 'text-green-600' : 'text-red-600'}`}>
                        {transaction.category === 'income' ? '+' : '-'}{formatCurrency(Math.abs(transaction.amount))}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{transaction.vendor || '-'}</td>
                      {isBoard && (
                        <>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {transaction.property_identifier || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {transaction.property_address || '-'}
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${linkedItemName ? 'text-gray-500' : 'text-red-500 font-medium'}`}>
                            {linkedItemName || '⚠️ NOT LINKED'}
                          </td>
                        </>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{transaction.reference_number || '-'}</td>
                      {isBoard && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex space-x-3">
                            <button
                              onClick={() => onEditTransaction(transaction)}
                              className="text-indigo-600 hover:text-indigo-900"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => onDeleteTransaction(transaction.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Balance Sheet View Component
const BalanceSheetView = ({ balanceSheet: initialBalanceSheet, isBoard, onAddItem, onRefresh, cashDetails }) => {
  const [dateFilter, setDateFilter] = useState(new Date().toISOString().split('T')[0]); // Default to today
  const [editingCash, setEditingCash] = useState(false);
  const [cashAmount, setCashAmount] = useState(0);
  const [cashAsOfDate, setCashAsOfDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [balanceSheet, setBalanceSheet] = useState(initialBalanceSheet);
  const [localCashDetails, setLocalCashDetails] = useState(cashDetails);
  const [allItems, setAllItems] = useState([]); // All balance sheet items
  const [editingItem, setEditingItem] = useState(null); // Item being edited
  const [editItemAmount, setEditItemAmount] = useState('');
  const [editItemDate, setEditItemDate] = useState('');

  // Fetch all balance sheet items on mount
  useEffect(() => {
    fetchAllItems();
  }, []);

  const fetchAllItems = async () => {
    try {
      const response = await api.get('/api/budget/balance-sheet/items');
      if (response.data.success) {
        setAllItems(response.data.items || []);
      }
    } catch (error) {
      console.error('Error fetching balance sheet items:', error);
    }
  };

  // Fetch balance sheet data when date filter changes
  useEffect(() => {
    fetchBalanceSheetForDate();
  }, [dateFilter]);

  const fetchBalanceSheetForDate = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/budget/balance-sheet', {
        params: { as_of_date: dateFilter }
      });
      if (response.data.success) {
        setBalanceSheet(response.data.balance_sheet);
        setLocalCashDetails(response.data.cash_details);
      }
    } catch (error) {
      console.error('Error fetching balance sheet:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch cash balance on component mount
  useEffect(() => {
    fetchCashBalance();
  }, []);

  const fetchCashBalance = async () => {
    try {
      const response = await api.get('/api/budget/balance-sheet/cash');
      if (response.data.success) {
        setCashAmount(response.data.amount || 0);
        setCashAsOfDate(response.data.as_of_date || '');
      }
    } catch (error) {
      console.error('Error fetching cash balance:', error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const handleUpdateCashBalance = async () => {
    try {
      setLoading(true);
      // Use the new beginning-balance endpoint with as_of_date
      const response = await api.put('/api/budget/config/beginning-balance', {
        beginning_balance: cashAmount,
        as_of_date: cashAsOfDate ? cashAsOfDate + 'T00:00:00' : new Date().toISOString()
      });

      if (response.data.success) {
        setEditingCash(false);
        alert(`Cash balance set to $${cashAmount.toLocaleString()} as of ${cashAsOfDate || 'today'}`);
        onRefresh();
        fetchBalanceSheetForDate(); // Refresh to show adjusted balance
        fetchAllItems(); // Refresh items list
      }
    } catch (error) {
      console.error('Error updating cash balance:', error);
      alert('Failed to update cash balance: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Handle editing a non-cash balance sheet item
  const handleUpdateItem = async () => {
    if (!editingItem) return;
    try {
      setLoading(true);
      const response = await api.put(`/api/budget/balance-sheet/items/${editingItem.id}`, {
        amount: parseFloat(editItemAmount) || 0,
        as_of_date: editItemDate || new Date().toISOString().split('T')[0]
      });
      if (response.data.success) {
        setEditingItem(null);
        setEditItemAmount('');
        setEditItemDate('');
        alert('Balance sheet item updated successfully!');
        onRefresh();
        fetchBalanceSheetForDate();
        fetchAllItems();
      }
    } catch (error) {
      console.error('Error updating balance sheet item:', error);
      const errMsg = error.response?.data?.detail || error.message;
      alert('Failed to update item: ' + errMsg);
    } finally {
      setLoading(false);
    }
  };

  if (!balanceSheet) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <p className="text-gray-500">No balance sheet data available.</p>
        {isBoard && (
          <button
            onClick={onAddItem}
            className="mt-4 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md text-sm font-medium"
          >
            Add Balance Sheet Item
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Date Filter and Actions */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-gray-700">As of Date:</span>
            <input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          
          {/* Cash Balance Editor with As-Of Date */}
          {isBoard && (
            <div className="flex flex-col space-y-2 bg-green-50 p-3 rounded-lg border border-green-200">
              <span className="text-sm font-medium text-gray-700">Set Base Cash Balance:</span>
              {editingCash ? (
                <div className="flex flex-col space-y-2">
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-500">Amount:</span>
                    <input
                      type="number"
                      step="0.01"
                      value={cashAmount}
                      onChange={(e) => setCashAmount(parseFloat(e.target.value) || 0)}
                      className="w-32 px-2 py-1 text-sm border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Amount"
                    />
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-500">As of:</span>
                    <input
                      type="date"
                      value={cashAsOfDate}
                      onChange={(e) => setCashAsOfDate(e.target.value)}
                      className="w-36 px-2 py-1 text-sm border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={handleUpdateCashBalance}
                      disabled={loading}
                      className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-xs disabled:opacity-50"
                    >
                      {loading ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={() => setEditingCash(false)}
                      className="bg-gray-500 hover:bg-gray-600 text-white px-3 py-1 rounded text-xs"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col space-y-1">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-semibold text-green-600">{formatCurrency(localCashDetails?.base_amount || cashAmount)}</span>
                    {localCashDetails?.base_as_of_date && (
                      <span className="text-xs text-gray-500">
                        (as of {new Date(localCashDetails.base_as_of_date).toLocaleDateString()})
                      </span>
                    )}
                    <button
                      onClick={() => {
                        setEditingCash(true);
                        setCashAmount(localCashDetails?.base_amount || 0);
                        setCashAsOfDate(localCashDetails?.base_as_of_date ? localCashDetails.base_as_of_date.split('T')[0] : '');
                      }}
                      className="text-indigo-600 hover:text-indigo-700 text-xs"
                    >
                      ✎ Edit
                    </button>
                  </div>
                  {localCashDetails?.adjusted_amount !== localCashDetails?.base_amount && (
                    <div className="text-xs text-blue-600">
                      Adjusted balance (with transactions): {formatCurrency(localCashDetails?.adjusted_amount)}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        
        {isBoard && (
          <button
            onClick={onAddItem}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md text-sm font-medium"
          >
            Add Balance Sheet Item
          </button>
        )}
      </div>

      {/* Balance Sheet Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm">🏦</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Assets</dt>
                  <dd className="text-lg font-medium text-gray-900">{formatCurrency(balanceSheet.total_assets)}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm">📋</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Liabilities</dt>
                  <dd className="text-lg font-medium text-gray-900">{formatCurrency(balanceSheet.total_liabilities)}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm">💎</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Equity</dt>
                  <dd className="text-lg font-medium text-gray-900">{formatCurrency(balanceSheet.total_equity)}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className={`w-8 h-8 ${balanceSheet.net_worth >= 0 ? 'bg-green-500' : 'bg-red-500'} rounded-full flex items-center justify-center`}>
                  <span className="text-white text-sm">{balanceSheet.net_worth >= 0 ? '📈' : '📉'}</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Net Worth</dt>
                  <dd className={`text-lg font-medium ${balanceSheet.net_worth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(balanceSheet.net_worth)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Cash Balance Note for Board */}
      {isBoard && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <span className="text-blue-500 mr-2">ℹ️</span>
            <div className="text-sm text-blue-800">
              <p className="font-medium">How Cash Balance Works:</p>
              <p>Cash = Beginning Balance (set as of a date) + Income Transactions - Expense Transactions</p>
              <p className="mt-1">To adjust cash, either update the Beginning Balance above or add transactions. If you need to make a manual adjustment, add a balance sheet adjustment item.</p>
            </div>
          </div>
        </div>
      )}

      {/* All Balance Sheet Items - showing items from allItems state */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">All Balance Sheet Items</h3>
          <p className="text-sm text-gray-600">As of {new Date(balanceSheet.as_of_date).toLocaleDateString()}</p>
        </div>
        <div className="p-6">
          {allItems.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No balance sheet items found. Add items below.</p>
          ) : (
            <div className="space-y-4">
              {/* Group items by category */}
              {['asset', 'liability', 'equity'].map(itemType => {
                const typeItems = allItems.filter(i => i.item_type === itemType);
                if (typeItems.length === 0) return null;
                return (
                  <div key={itemType} className="space-y-2">
                    <h4 className="font-medium text-gray-900 capitalize border-b border-gray-200 pb-2">
                      {itemType === 'asset' ? '📊 Assets' : itemType === 'liability' ? '📋 Liabilities' : '💎 Equity'}
                    </h4>
                    <div className="space-y-2 ml-4">
                      {typeItems.map(item => (
                        <div key={item.id} className="flex justify-between items-center py-2 border-b border-gray-100">
                          <div className="flex flex-col">
                            <span className="text-sm font-medium text-gray-700">{item.name}</span>
                            <span className="text-xs text-gray-500">{item.category?.replace(/_/g, ' ')}</span>
                            {item.as_of_date && (
                              <span className="text-xs text-gray-400">As of: {new Date(item.as_of_date).toLocaleDateString()}</span>
                            )}
                          </div>
                          <div className="flex items-center space-x-3">
                            {editingItem?.id === item.id ? (
                              <div className="flex flex-col space-y-1">
                                <div className="flex items-center space-x-2">
                                  <input
                                    type="number"
                                    step="0.01"
                                    value={editItemAmount}
                                    onChange={(e) => setEditItemAmount(e.target.value)}
                                    className="w-28 px-2 py-1 text-sm border border-gray-300 rounded"
                                    placeholder="Amount"
                                  />
                                  <input
                                    type="date"
                                    value={editItemDate}
                                    onChange={(e) => setEditItemDate(e.target.value)}
                                    className="w-32 px-2 py-1 text-sm border border-gray-300 rounded"
                                  />
                                </div>
                                <div className="flex space-x-2">
                                  <button onClick={handleUpdateItem} disabled={loading} className="text-green-600 hover:text-green-800 text-xs">✓ Save</button>
                                  <button onClick={() => setEditingItem(null)} className="text-red-600 hover:text-red-800 text-xs">✕ Cancel</button>
                                </div>
                              </div>
                            ) : (
                              <>
                                {/* For Cash and Cash Equivalents, show the ADJUSTED amount */}
                                {item.name === 'Cash and Cash Equivalents' ? (
                                  <>
                                    <span className="text-sm font-bold text-blue-600">
                                      {formatCurrency(localCashDetails?.adjusted_amount || item.amount)}
                                    </span>
                                    <span className="text-xs text-blue-500">(Auto-calculated: Base + Transactions)</span>
                                  </>
                                ) : (
                                  <>
                                    <span className="text-sm font-bold text-gray-900">{formatCurrency(item.amount)}</span>
                                    {isBoard && (
                                      <button
                                        onClick={() => {
                                          setEditingItem(item);
                                          setEditItemAmount(item.amount?.toString() || '0');
                                          setEditItemDate(item.as_of_date ? item.as_of_date.split('T')[0] : '');
                                        }}
                                        className="text-indigo-600 hover:text-indigo-800 text-xs"
                                      >
                                        ✎ Edit
                                      </button>
                                    )}
                                  </>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

// Reports View Component
const ReportsView = ({ budgetSummary, transactions, balanceSheet, budgetItems, config, isBoard }) => {
  const [reportType, setReportType] = useState('profit-loss');
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0], // Start of year
    end: new Date().toISOString().split('T')[0] // Today
  });
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [homeownerInfo, setHomeownerInfo] = useState('');
  const [editingHomeownerInfo, setEditingHomeownerInfo] = useState(false);
  const [savingHomeownerInfo, setSavingHomeownerInfo] = useState(false);

  // Load homeowner info on component mount
  useEffect(() => {
    if (isBoard) {
      loadHomeownerInfo();
    }
  }, [isBoard]);

  const loadHomeownerInfo = async () => {
    try {
      const response = await api.get('/api/budget/homeowner-info');
      if (response.data && response.data.info) {
        setHomeownerInfo(response.data.info);
      }
    } catch (error) {
      console.error('Error loading homeowner info:', error);
    }
  };

  const saveHomeownerInfo = async () => {
    setSavingHomeownerInfo(true);
    try {
      await api.post('/api/budget/homeowner-info', {
        info: homeownerInfo
      });
      setEditingHomeownerInfo(false);
      alert('✅ Homeowner information saved successfully!');
    } catch (error) {
      console.error('Error saving homeowner info:', error);
      alert('Failed to save homeowner information: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSavingHomeownerInfo(false);
    }
  };

  const generateReport = async () => {
    setLoading(true);
    try {
      if (reportType === 'profit-loss') {
        const response = await api.get('/api/budget/reports/profit-loss', {
          params: {
            start_date: dateRange.start,
            end_date: dateRange.end
          }
        });
        
        if (response.data.success) {
          setReportData(response.data.report);
        }
      } else if (reportType === 'budget-variance') {
        // Call backend API for budget variance report
        const response = await api.get('/api/budget/reports/budget-variance');
        
        if (response.data.success) {
          setReportData(response.data.report);
        }
      } else if (reportType === 'cash-flow') {
        // Call backend API for cash flow report with beginning balance
        const response = await api.get('/api/budget/reports/cash-flow', {
          params: {
            start_date: dateRange.start,
            end_date: dateRange.end
          }
        });
        
        if (response.data.success) {
          setReportData(response.data.report);
        }
      }
    } catch (error) {
      console.error('Error generating report:', error);
      alert('Failed to generate report: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const generateBudgetVarianceReport = () => {
    const incomeItems = budgetItems.filter(item => item.category === 'income');
    const expenseItems = budgetItems.filter(item => item.category === 'expense');
    
    const incomeVariances = incomeItems.map(item => ({
      description: item.description,
      budgeted: item.budgeted_amount,
      actual: item.actual_amount,
      variance: item.actual_amount - item.budgeted_amount,
      variancePercent: item.budgeted_amount ? ((item.actual_amount - item.budgeted_amount) / item.budgeted_amount * 100) : 0
    }));
    
    const expenseVariances = expenseItems.map(item => ({
      description: item.description,
      budgeted: item.budgeted_amount,
      actual: item.actual_amount,
      variance: item.budgeted_amount - item.actual_amount, // For expenses, positive is good
      variancePercent: item.budgeted_amount ? ((item.budgeted_amount - item.actual_amount) / item.budgeted_amount * 100) : 0
    }));
    
    return {
      type: 'budget-variance',
      period: { start_date: dateRange.start, end_date: dateRange.end },
      income: incomeVariances,
      expenses: expenseVariances,
      totals: {
        totalIncomeBudgeted: incomeItems.reduce((sum, item) => sum + item.budgeted_amount, 0),
        totalIncomeActual: incomeItems.reduce((sum, item) => sum + item.actual_amount, 0),
        totalExpensesBudgeted: expenseItems.reduce((sum, item) => sum + item.budgeted_amount, 0),
        totalExpensesActual: expenseItems.reduce((sum, item) => sum + item.actual_amount, 0)
      }
    };
  };

  const generateCashFlowReport = () => {
    // Filter transactions by date range
    const filteredTransactions = transactions.filter(t => {
      if (!t.transaction_date) return false;
      try {
        const transactionDate = new Date(t.transaction_date).toISOString().split('T')[0];
        return transactionDate >= dateRange.start && transactionDate <= dateRange.end;
      } catch {
        return false;
      }
    });
    
    // Group by month
    const monthlyData = {};
    filteredTransactions.forEach(transaction => {
      try {
        const dateObj = new Date(transaction.transaction_date);
        if (isNaN(dateObj.getTime())) return; // Skip invalid dates
        
        const month = dateObj.toISOString().substring(0, 7); // YYYY-MM
        if (!monthlyData[month]) {
          monthlyData[month] = { income: 0, expenses: 0 };
        }
        
        if (transaction.category === 'income') {
          monthlyData[month].income += transaction.amount || 0;
        } else {
          monthlyData[month].expenses += transaction.amount || 0;
        }
      } catch {
        // Skip transactions with invalid dates
      }
    });
    
    return {
      type: 'cash-flow',
      period: { start_date: dateRange.start, end_date: dateRange.end },
      monthlyData: monthlyData,
      totals: {
        totalIncome: filteredTransactions.filter(t => t.category === 'income').reduce((sum, t) => sum + (t.amount || 0), 0),
        totalExpenses: filteredTransactions.filter(t => t.category === 'expense').reduce((sum, t) => sum + (t.amount || 0), 0)
      }
    };
  };

  const exportReport = () => {
    if (!reportData) {
      alert('Please generate a report first');
      return;
    }
    
    let csvContent = '';
    
    if (reportType === 'profit-loss' && reportData.income && reportData.expenses) {
      csvContent = 'Profit & Loss Report\n';
      csvContent += `Period: ${reportData.period.start_date} to ${reportData.period.end_date}\n\n`;
      
      csvContent += 'INCOME\n';
      csvContent += 'Category,Amount\n';
      Object.entries(reportData.income).forEach(([category, amount]) => {
        csvContent += `${category},${amount}\n`;
      });
      
      csvContent += '\nEXPENSES\n';
      csvContent += 'Category,Amount\n';
      Object.entries(reportData.expenses).forEach(([category, amount]) => {
        csvContent += `${category},${amount}\n`;
      });
      
      csvContent += '\nSUMMARY\n';
      csvContent += `Total Income,${reportData.totals.total_income}\n`;
      csvContent += `Total Expenses,${reportData.totals.total_expenses}\n`;
      csvContent += `Net Income,${reportData.totals.net_income}\n`;
    }
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${reportType}-report-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const [publishLoading, setPublishLoading] = useState(false);
  const [reportNotes, setReportNotes] = useState('');
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [pendingPublishAction, setPendingPublishAction] = useState(null);

  const publishReportToDocuments = async (notes = '') => {
    if (!reportData) {
      alert('Please generate a report first');
      return;
    }

    setPublishLoading(true);
    try {
      // Determine period type based on date range
      // Parse as local date (not UTC) by splitting the string
      const [sy, sm, sd] = dateRange.start.split('-').map(Number);
      const [ey, em, ed] = dateRange.end.split('-').map(Number);
      const startDate = new Date(sy, sm - 1, sd);
      const endDate = new Date(ey, em - 1, ed);
      const monthDiff = (endDate.getFullYear() - startDate.getFullYear()) * 12 + (endDate.getMonth() - startDate.getMonth());
      
      let periodType = 'monthly';
      if (monthDiff >= 11) {
        periodType = 'full-year';
      } else if (monthDiff >= 5) {
        periodType = '6-month';
      } else if (monthDiff >= 2) {
        periodType = 'quarterly';
      }

      const response = await api.post('/api/budget/reports/publish', {
        report_type: reportType,
        period_type: periodType,
        start_date: dateRange.start,
        end_date: dateRange.end,
        report_content: reportData,
        board_notes: notes // Add notes to the report
      });

      if (response.data.success) {
        alert(`✅ ${response.data.title} has been published to the Documents tab successfully!`);
        setReportNotes(''); // Clear notes after successful publish
      }
    } catch (error) {
      console.error('Error publishing report:', error);
      alert('Failed to publish report: ' + (error.response?.data?.detail || error.message));
    } finally {
      setPublishLoading(false);
    }
  };

  const publishComprehensiveReport = async (notes = '') => {
    setPublishLoading(true);
    try {
      // Determine period type based on date range
      // Parse as local date (not UTC) by splitting the string
      const [sy, sm, sd] = dateRange.start.split('-').map(Number);
      const [ey, em, ed] = dateRange.end.split('-').map(Number);
      const startDate = new Date(sy, sm - 1, sd);
      const endDate = new Date(ey, em - 1, ed);
      const monthDiff = (endDate.getFullYear() - startDate.getFullYear()) * 12 + (endDate.getMonth() - startDate.getMonth());
      
      let periodType = 'monthly';
      if (monthDiff >= 11) {
        periodType = 'full-year';
      } else if (monthDiff >= 5) {
        periodType = '6-month';
      } else if (monthDiff >= 2) {
        periodType = 'quarterly';
      }

      const response = await api.post('/api/budget/reports/publish', {
        report_type: 'comprehensive',
        period_type: periodType,
        start_date: dateRange.start,
        end_date: dateRange.end,
        report_content: {}, // Content will be generated from database
        board_notes: notes // Add notes to the report
      });

      if (response.data.success) {
        alert(`✅ ${response.data.title} has been published to the Documents tab successfully!`);
        setReportNotes(''); // Clear notes after successful publish
      }
    } catch (error) {
      console.error('Error publishing comprehensive report:', error);
      alert('Failed to publish comprehensive report: ' + (error.response?.data?.detail || error.message));
    } finally {
      setPublishLoading(false);
    }
  };

  const handlePublishWithNotes = (action) => {
    setPendingPublishAction(action);
    setShowNotesModal(true);
  };

  const confirmPublishWithNotes = () => {
    if (pendingPublishAction === 'individual') {
      publishReportToDocuments(reportNotes);
    } else if (pendingPublishAction === 'comprehensive') {
      publishComprehensiveReport(reportNotes);
    }
    setShowNotesModal(false);
    setPendingPublishAction(null);
  };
  return (
    <div className="space-y-6">
      {/* Homeowner Information Banner - Visible to All */}
      {homeownerInfo && (
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-blue-800">ℹ️ Financial Reporting Information</h3>
              <div className="mt-2 text-sm text-blue-700 whitespace-pre-wrap">
                {homeownerInfo}
              </div>
            </div>
            {isBoard && (
              <button
                onClick={() => setEditingHomeownerInfo(true)}
                className="ml-3 text-sm text-blue-600 hover:text-blue-800 underline flex-shrink-0"
              >
                Edit
              </button>
            )}
          </div>
        </div>
      )}

      {/* Board-Only: Edit Homeowner Info */}
      {isBoard && !homeownerInfo && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-yellow-800">No Homeowner Information Set</h3>
              <p className="mt-1 text-sm text-yellow-700">
                Add important financial information to display to homeowners on the Reports tab.
              </p>
              <button
                onClick={() => setEditingHomeownerInfo(true)}
                className="mt-2 text-sm font-medium text-yellow-800 hover:text-yellow-900 underline"
              >
                + Add Information
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Homeowner Info Modal */}
      {editingHomeownerInfo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6">
            <h2 className="text-xl font-bold mb-4">Edit Homeowner Information</h2>
            <p className="text-sm text-gray-600 mb-4">
              This information will be displayed at the top of the Reports tab for all homeowners to see. 
              Use this to inform them about financial requirements, audit schedules, or reporting guidelines.
            </p>
            <textarea
              value={homeownerInfo}
              onChange={(e) => setHomeownerInfo(e.target.value)}
              placeholder="Example: The income of the HOA is lower than $150,000 and therefore does not require audited financials or quarterly reports, only cash receipts per FL Statute §720.303(7)(b)(1)"
              className="w-full h-40 p-3 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 mb-4"
            />
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => {
                  setEditingHomeownerInfo(false);
                  loadHomeownerInfo(); // Reload to revert changes
                }}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={saveHomeownerInfo}
                disabled={savingHomeownerInfo}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {savingHomeownerInfo ? 'Saving...' : 'Save Information'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Report Controls */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Financial Reports</h3>
          <p className="text-sm text-gray-500 mt-1">Generate and export financial reports at any time</p>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {/* Report Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Report Type</label>
              <select
                value={reportType}
                onChange={(e) => setReportType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="profit-loss">Profit & Loss</option>
                <option value="budget-variance">Budget Variance</option>
                <option value="cash-flow">Cash Flow</option>
                <option value="comprehensive">📊 Comprehensive Financial Report</option>
              </select>
            </div>

            {/* Start Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange({...dateRange, start: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            {/* End Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange({...dateRange, end: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              />
              {reportType === 'profit-loss' && (
                <p className="text-xs text-gray-500 mt-1">
                  💡 Tip: Transactions appear by end of day. Choose one day after your latest transaction date.
                </p>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-end space-x-2">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  generateReport();
                }}
                disabled={loading}
                className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md font-medium disabled:opacity-50 relative z-10"
                type="button"
              >
                {loading ? 'Generating...' : 'Generate'}
              </button>
              {reportData && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    exportReport();
                  }}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md font-medium relative z-10"
                  type="button"
                >
                  Export CSV
                </button>
              )}
              {reportData && isBoard && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handlePublishWithNotes('individual');
                  }}
                  disabled={publishLoading}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium disabled:opacity-50 relative z-10"
                  type="button"
                >
                  {publishLoading ? 'Publishing...' : '📄 Publish to Documents'}
                </button>
              )}
            </div>
            
            {/* Notes Modal */}
            {showNotesModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Add Board Notes to Report</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Add any board notes or comments that should be included with this financial report.
                  </p>
                  <textarea
                    value={reportNotes}
                    onChange={(e) => setReportNotes(e.target.value)}
                    placeholder="Enter board notes (optional)..."
                    className="w-full h-32 p-3 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <div className="flex justify-end gap-2 mt-4">
                    <button
                      onClick={() => {
                        setShowNotesModal(false);
                        setPendingPublishAction(null);
                        setReportNotes('');
                      }}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={confirmPublishWithNotes}
                      disabled={publishLoading}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50"
                    >
                      {publishLoading ? 'Publishing...' : 'Publish Report'}
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Comprehensive Report Quick Actions - Board Members Only */}
            {isBoard && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                <h4 className="text-sm font-medium text-blue-800 mb-2">📊 Professional Financial Reports</h4>
                <p className="text-xs text-blue-600 mb-3">Generate comprehensive financial reports with HOA branding for official distribution</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => handlePublishWithNotes('comprehensive')}
                    disabled={publishLoading}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50"
                  >
                    {publishLoading ? 'Generating...' : '📈 Comprehensive Report'}
                  </button>
                  <div className="text-xs text-blue-600 self-center">
                    Includes P&L, Budget Variance, and Balance Sheet
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Report Display */}
      {reportData && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 capitalize">
              {reportType.replace('-', ' ')} Report
            </h3>
            <p className="text-sm text-gray-600">
              Period: {(() => {
                // Parse date as local (not UTC) by splitting the string
                const [sy, sm, sd] = dateRange.start.split('-').map(Number);
                const [ey, em, ed] = dateRange.end.split('-').map(Number);
                const startFormatted = new Date(sy, sm - 1, sd).toLocaleDateString();
                const endFormatted = new Date(ey, em - 1, ed).toLocaleDateString();
                return `${startFormatted} - ${endFormatted}`;
              })()}
            </p>
          </div>
          <div className="p-6">
            {reportType === 'profit-loss' && reportData.income && reportData.expenses && (
              <ProfitLossReport data={reportData} />
            )}
            {reportType === 'budget-variance' && (
              <BudgetVarianceReport data={reportData} />
            )}
            {reportType === 'cash-flow' && (
              <CashFlowReport data={reportData} />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Sub-components for different report types
const ProfitLossReport = ({ data }) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  // Safety check for data structure
  if (!data || !data.income || !data.expenses || !data.totals) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No profit & loss data available for the selected period.</p>
      </div>
    );
  }

  const income = data.income || {};
  const expenses = data.expenses || {};
  const totals = data.totals || { total_income: 0, total_expenses: 0, net_income: 0 };

  return (
    <div className="space-y-6">
      {/* Income Section */}
      <div>
        <h4 className="text-lg font-medium text-green-700 mb-3">Income</h4>
        <div className="space-y-2">
          {Object.keys(income).length === 0 ? (
            <p className="text-gray-500 py-2">No income recorded for this period.</p>
          ) : (
            Object.entries(income).map(([category, amount]) => (
              <div key={category} className="flex justify-between py-2 border-b border-gray-100">
                <span className="capitalize">{category.replace('_', ' ')}</span>
                <span className="font-medium text-green-600">{formatCurrency(amount)}</span>
              </div>
            ))
          )}
          <div className="flex justify-between py-3 border-t-2 border-green-200 font-bold text-green-700">
            <span>Total Income</span>
            <span>{formatCurrency(totals.total_income)}</span>
          </div>
        </div>
      </div>

      {/* Expenses Section */}
      <div>
        <h4 className="text-lg font-medium text-red-700 mb-3">Expenses</h4>
        <div className="space-y-2">
          {Object.keys(expenses).length === 0 ? (
            <p className="text-gray-500 py-2">No expenses recorded for this period.</p>
          ) : (
            Object.entries(expenses).map(([category, amount]) => (
              <div key={category} className="flex justify-between py-2 border-b border-gray-100">
                <span className="capitalize">{category.replace('_', ' ')}</span>
                <span className="font-medium text-red-600">{formatCurrency(amount)}</span>
              </div>
            ))
          )}
          <div className="flex justify-between py-3 border-t-2 border-red-200 font-bold text-red-700">
            <span>Total Expenses</span>
            <span>{formatCurrency(totals.total_expenses)}</span>
          </div>
        </div>
      </div>

      {/* Net Income */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className={`flex justify-between items-center text-xl font-bold ${
          totals.net_income >= 0 ? 'text-green-700' : 'text-red-700'
        }`}>
          <span>Net Income</span>
          <span>{formatCurrency(totals.net_income)}</span>
        </div>
      </div>
    </div>
  );
};

const BudgetVarianceReport = ({ data }) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  // Safety check for data structure
  if (!data || !data.income || !data.expenses) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No budget variance data available.</p>
      </div>
    );
  }

  const income = Array.isArray(data.income) ? data.income : [];
  const expenses = Array.isArray(data.expenses) ? data.expenses : [];

  return (
    <div className="space-y-6">
      {/* Income Variances */}
      <div>
        <h4 className="text-lg font-medium text-green-700 mb-3">Income Variances</h4>
        {income.length === 0 ? (
          <p className="text-gray-500 py-4">No income variances to display.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Budgeted</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actual</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Variance</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Variance %</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {income.map((item, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.description || 'N/A'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(item.budgeted || 0)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(item.actual || 0)}</td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${(item.variance || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(item.variance || 0)}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${(item.variance || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {(item.variancePercent || 0).toFixed(1)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Expense Variances */}
      <div>
        <h4 className="text-lg font-medium text-red-700 mb-3">Expense Variances</h4>
        {expenses.length === 0 ? (
          <p className="text-gray-500 py-4">No expense variances to display.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Budgeted</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actual</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Variance</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Variance %</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {expenses.map((item, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.description || 'N/A'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(item.budgeted || 0)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(item.actual || 0)}</td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${(item.variance || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(item.variance || 0)}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${(item.variance || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {(item.variancePercent || 0).toFixed(1)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const CashFlowReport = ({ data }) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  // Safety check for data structure
  if (!data || !data.monthlyData) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No cash flow data available for the selected period.</p>
      </div>
    );
  }

  const monthlyData = data.monthlyData || {};
  const totals = data.totals || { totalIncome: 0, totalExpenses: 0 };
  const startingCash = data.starting_cash || 0;
  const cashBalanceEntry = data.cash_balance_entry;
  const endingCash = totals.endingCash || 0;

  // Calculate running balance per month
  let runningBalance = startingCash;

  // Check if cash balance was entered during the report period
  const showCashBalanceEntry = cashBalanceEntry?.in_period && cashBalanceEntry?.amount > 0;

  // Format cash balance entry date for display
  let cashEntryMonth = null;
  if (showCashBalanceEntry && cashBalanceEntry.date) {
    try {
      const dateStr = cashBalanceEntry.date.split('T')[0];
      cashEntryMonth = dateStr.substring(0, 7); // YYYY-MM
    } catch (e) {
      // Ignore date parsing errors
    }
  }

  // Combine monthly data with cash entry if applicable
  const allEntries = [];
  
  // Add cash balance entry if it falls within period
  if (showCashBalanceEntry && cashEntryMonth) {
    allEntries.push({
      key: cashEntryMonth + '-CASH',
      month: cashEntryMonth,
      isCashEntry: true,
      amount: cashBalanceEntry.amount,
      date: cashBalanceEntry.date
    });
  }
  
  // Add regular monthly entries
  Object.entries(monthlyData).forEach(([month, values]) => {
    allEntries.push({
      key: month,
      month: month,
      isCashEntry: false,
      income: values.income || 0,
      expenses: values.expenses || 0
    });
  });

  // Sort all entries by month
  allEntries.sort((a, b) => a.month.localeCompare(b.month));

  return (
    <div className="space-y-6">
      {/* Starting Cash Balance */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex justify-between items-center">
          <span className="text-sm font-medium text-blue-800">Beginning Cash Balance (Start of Period)</span>
          <span className="text-xl font-bold text-blue-600">{formatCurrency(startingCash)}</span>
        </div>
      </div>

      {/* Monthly Breakdown */}
      <div>
        <h4 className="text-lg font-medium text-gray-900 mb-3">Monthly Cash Flow</h4>
        {allEntries.length === 0 ? (
          <p className="text-gray-500 text-center py-4">No transactions found for the selected period.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Month</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Income</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expenses</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Net</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-blue-600 uppercase tracking-wider bg-blue-50">Running Balance</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {allEntries.map((entry) => {
                  if (entry.isCashEntry) {
                    // Cash Balance Entry row
                    runningBalance += entry.amount;
                    const entryDate = new Date(entry.date);
                    return (
                      <tr key={entry.key} className="bg-green-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-800">
                          💰 Cash Balance Entry ({entryDate.toLocaleDateString()})
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-bold">
                          {formatCurrency(entry.amount)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">-</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                          {formatCurrency(entry.amount)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium bg-blue-50 text-blue-600">
                          {formatCurrency(runningBalance)}
                        </td>
                      </tr>
                    );
                  } else {
                    // Regular monthly entry
                    const net = entry.income - entry.expenses;
                    runningBalance += net;
                    
                    // Safely format month display
                    let monthDisplay;
                    try {
                      const [year, monthNum] = entry.month.split('-').map(Number);
                      const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                                         'July', 'August', 'September', 'October', 'November', 'December'];
                      monthDisplay = `${monthNames[monthNum - 1]} ${year}`;
                    } catch {
                      monthDisplay = entry.month;
                    }
                    
                    return (
                      <tr key={entry.key}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {monthDisplay}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">{formatCurrency(entry.income)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600">{formatCurrency(entry.expenses)}</td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${net >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {formatCurrency(net)}
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium bg-blue-50 ${runningBalance >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                          {formatCurrency(runningBalance)}
                        </td>
                      </tr>
                    );
                  }
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Summary */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-sm text-gray-600">Beginning Balance</div>
            <div className="text-lg font-bold text-blue-600">{formatCurrency(startingCash)}</div>
          </div>
          <div>
            <div className="text-sm text-gray-600">Total Income</div>
            <div className="text-lg font-bold text-green-600">{formatCurrency(totals.totalIncome)}</div>
          </div>
          <div>
            <div className="text-sm text-gray-600">Total Expenses</div>
            <div className="text-lg font-bold text-red-600">{formatCurrency(totals.totalExpenses)}</div>
          </div>
          <div>
            <div className="text-sm text-gray-600">Ending Balance</div>
            <div className={`text-lg font-bold ${endingCash >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
              {formatCurrency(endingCash)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


// Set Budget Modal Component
const SetBudgetModal = ({ form, setForm, onSubmit, onClose, loading, config }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-500 to-blue-600">
          <h2 className="text-xl font-bold text-white">📊 Set Official Budget Document</h2>
          <p className="text-blue-100 text-sm mt-1">Create a professional PDF budget report for your fiscal year</p>
        </div>
        
        <form onSubmit={onSubmit} className="p-6 space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <h3 className="font-semibold text-blue-900 mb-2">📄 Document Details</h3>
            <p className="text-sm text-blue-700">
              This will generate a professional PDF document with all current budget items, income/expense summaries, and your notes.
              The document will be saved to <strong>Documents → Financial Documents</strong> and visible to all homeowners.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Budget Type *
              </label>
              <select
                value={form.budget_type}
                onChange={(e) => setForm({...form, budget_type: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="initial">Initial Budget</option>
                <option value="mid_year">Mid-Year Budget</option>
                <option value="proposed">Proposed Budget</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {form.budget_type === 'initial' && 'Shows only budgeted amounts'}
                {form.budget_type === 'mid_year' && 'Shows both budget and actual amounts'}
                {form.budget_type === 'proposed' && 'Shows budgeted amounts alongside proposed amounts for approval'}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fiscal Year *
              </label>
              <input
                type="text"
                value={form.fiscal_year}
                onChange={(e) => setForm({...form, fiscal_year: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="2025"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Date *
              </label>
              <input
                type="date"
                value={form.start_date}
                onChange={(e) => setForm({...form, start_date: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Date *
              </label>
              <input
                type="date"
                value={form.end_date}
                onChange={(e) => setForm({...form, end_date: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
          </div>

          <div>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={form.show_prior_year}
                onChange={(e) => setForm({...form, show_prior_year: e.target.checked})}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700">Show Prior Year Numbers</span>
            </label>
            <p className="text-xs text-gray-500 mt-1 ml-6">Include prior year budget and actual columns in the report</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes (Optional)
            </label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({...form, notes: e.target.value})}
              rows="4"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="Add any notes about this budget period, variances, or important highlights..."
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Creating Document...' : '📄 Create Budget Document'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Rollover Budget Modal Component
const RolloverBudgetModal = ({ action, setAction, onConfirm, onClose, loading }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg max-w-2xl w-full my-8 max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-purple-500 to-purple-600 flex-shrink-0">
          <h2 className="text-xl font-bold text-white">🔄 Rollover Budget to New Year</h2>
          <p className="text-purple-100 text-sm mt-1">Move current year data to prior year columns</p>
        </div>
        
        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h3 className="font-semibold text-yellow-900 mb-2">⚠️ Important Information</h3>
            <ul className="text-sm text-yellow-800 space-y-2">
              <li>• Current budget amounts → Prior Year Budget column</li>
              <li>• Current actual amounts → Prior Year Actual column</li>
              <li>• A save point will be created for restoration if needed</li>
              <li>• This action can be reversed by Platform Admin in God Mode</li>
            </ul>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
            <h3 className="font-semibold text-gray-900 mb-2">What should we do with CY Budget?</h3>
            
            <label className="flex items-start space-x-3 p-3 border-2 rounded-lg cursor-pointer hover:bg-gray-50"
              style={{borderColor: action === 'keep_actuals' ? '#9333EA' : '#E5E7EB'}}
            >
              <input
                type="radio"
                checked={action === 'keep_actuals'}
                onChange={() => setAction('keep_actuals')}
                className="mt-1"
              />
              <div>
                <div className="font-medium text-gray-900">Keep CY Budget</div>
                <div className="text-sm text-gray-600 mt-1">
                  Keep current CY Budget values in the new period's CY Budget column. Recommended for continuity.
                </div>
              </div>
            </label>

            <label className="flex items-start space-x-3 p-3 border-2 rounded-lg cursor-pointer hover:bg-gray-50"
              style={{borderColor: action === 'clear_actuals' ? '#9333EA' : '#E5E7EB'}}
            >
              <input
                type="radio"
                checked={action === 'clear_actuals'}
                onChange={() => setAction('clear_actuals')}
                className="mt-1"
              />
              <div>
                <div className="font-medium text-gray-900">Use Proposed Budget</div>
                <div className="text-sm text-gray-600 mt-1">
                  Move Proposed Budget values to CY Budget for the new period. Use if you've prepared proposed amounts.
                </div>
              </div>
            </label>
          </div>

          {action === 'clear_actuals' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-900 mb-2">ℹ️ Note</h3>
              <p className="text-sm text-blue-800">
                Proposed Budget values will be moved to CY Budget. Old CY Budget → PY Budget. 
                Actuals are calculated from transactions and preserved.
              </p>
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-3 px-6 py-4 border-t bg-gray-50 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50"
            disabled={loading}
          >
            {loading ? 'Processing...' : '🔄 Confirm Rollover'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Restore Rollover Modal Component
const RestoreRolloverModal = ({ restorePoints, onRestore, onClose, loading }) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="restore-rollover-modal">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-amber-500 to-amber-600">
          <h2 className="text-xl font-bold text-white">↩️ Restore Previous Rollover</h2>
          <p className="text-amber-100 text-sm mt-1">Undo a budget rollover by restoring from a save point</p>
        </div>
        
        <div className="p-6 space-y-4 overflow-y-auto max-h-[60vh]">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h3 className="font-semibold text-yellow-900 mb-2">⚠️ Important Information</h3>
            <ul className="text-sm text-yellow-800 space-y-2">
              <li>• Restoring will revert all budget items to the saved state</li>
              <li>• Current budget and actual values will be overwritten</li>
              <li>• This action cannot be undone</li>
            </ul>
          </div>

          {restorePoints.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-gray-400 text-5xl mb-4">📭</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Save Points Available</h3>
              <p className="text-gray-500 text-sm">
                Save points are created automatically when you perform a budget rollover.
                <br />
                No rollovers have been performed yet.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <h3 className="font-semibold text-gray-900">Available Save Points ({restorePoints.length})</h3>
              {restorePoints.map((sp) => (
                <div 
                  key={sp.id} 
                  className="border rounded-lg p-4 bg-white border-gray-200 hover:border-amber-300"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">
                        {sp.action === 'pre_rollover' ? '🔄 Pre-Rollover Save Point' : 'Save Point'}
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        Created: {formatDate(sp.created_at)}
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        Items saved: {sp.item_count || 'N/A'}
                      </div>
                      {sp.notes && (
                        <div className="text-sm text-gray-500 mt-1 italic">
                          {sp.notes}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => onRestore(sp.id)}
                      disabled={loading}
                      className="px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-50"
                      data-testid={`restore-point-btn-${sp.id}`}
                    >
                      {loading ? 'Restoring...' : '↩️ Restore'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              disabled={loading}
              data-testid="restore-modal-close-btn"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};


// Budget Item Modal Component
const BudgetItemModal = ({ form, setForm, onSubmit, onClose, loading, isEdit, config }) => {
  const subcategories = {
    income: ['dues', 'special_assessments', 'late_fees', 'interest', 'other'],
    expense: ['maintenance', 'landscaping', 'utilities', 'insurance', 'management', 'legal', 'office', 'repairs', 'reserves', 'other']
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onSubmit(e);
  };

  const handleClose = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onClose();
  };
  
  // Check if this is a dues item
  const isDuesItem = form.subcategory === 'dues';

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9999]" onClick={handleClose}>
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            {isEdit ? 'Edit Budget Item' : 'Add Budget Item'}
          </h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
            type="button"
          >
            ×
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Category</label>
              <select
                value={form.category}
                onChange={(e) => setForm({...form, category: e.target.value, subcategory: ''})}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="income">Income</option>
                <option value="expense">Expense</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Subcategory</label>
              <select
                value={form.subcategory}
                onChange={(e) => setForm({...form, subcategory: e.target.value})}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="">Select subcategory</option>
                {subcategories[form.category]?.map(sub => (
                  <option key={sub} value={sub} className="capitalize">
                    {sub.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700">Description</label>
            <input
              type="text"
              required
              value={form.description}
              onChange={(e) => setForm({...form, description: e.target.value})}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g., Monthly landscaping services"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Budgeted Amount ($)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                required
                value={form.budgeted_amount}
                onChange={(e) => setForm({...form, budgeted_amount: e.target.value})}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Recurrence</label>
              <select
                value={form.recurrence}
                onChange={(e) => setForm({...form, recurrence: e.target.value})}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="yearly">Yearly</option>
                <option value="one_time">One Time</option>
              </select>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700">Notes (Optional)</label>
            <textarea
              rows="3"
              value={form.notes}
              onChange={(e) => setForm({...form, notes: e.target.value})}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              placeholder="Additional notes about this budget item..."
            />
          </div>
          
          {/* Auto-Calc toggle for Dues items */}
          {isDuesItem && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={form.auto_calc !== false}
                  onChange={(e) => setForm({...form, auto_calc: e.target.checked})}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <div>
                  <span className="text-sm font-medium text-gray-700">Auto-calculate from unit count</span>
                  <p className="text-xs text-gray-500">
                    {form.auto_calc !== false 
                      ? `Amount will be calculated as: ${config?.total_units || 0} units × dues rate`
                      : 'You can enter a custom amount instead of auto-calculation'
                    }
                  </p>
                </div>
              </label>
            </div>
          )}
          
          {/* Proposed Amount field */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Proposed Amount (Optional)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={form.proposed_amount || ''}
              onChange={(e) => setForm({...form, proposed_amount: e.target.value})}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-orange-500 focus:border-orange-500"
              placeholder="Enter proposed budget amount for approval"
            />
            <p className="text-xs text-gray-500 mt-1">Use this for proposed budget reports before approval</p>
          </div>
          
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {loading ? 'Saving...' : (isEdit ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Transaction Modal Component
const TransactionModal = ({ form, setForm, budgetItems, onSubmit, onClose, loading, isEditing = false, isBoard = false }) => {
  const subcategories = {
    income: ['dues', 'special_assessments', 'late_fees', 'interest', 'fines', 'event_income', 'other_income'],
    expense: ['maintenance', 'landscaping', 'utilities', 'insurance', 'management', 'legal', 'office', 'repairs', 'reserves', 'other']
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onSubmit(e);
  };

  const handleClose = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    onClose();
  };

  return (
    <div 
      className="fixed inset-0 bg-gray-600 bg-opacity-50 z-[9999] flex items-start justify-center overflow-y-auto py-4" 
      onClick={handleClose}
      style={{ touchAction: 'pan-y' }}
    >
      <div 
        className="relative mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white my-auto"
        onClick={(e) => e.stopPropagation()}
        style={{ maxHeight: 'calc(100vh - 2rem)', overflowY: 'auto' }}
      >
        <div className="flex items-center justify-between mb-4 sticky top-0 bg-white pb-2 border-b z-10">
          <h3 className="text-lg font-medium text-gray-900">{isEditing ? 'Edit Transaction' : 'Add Transaction'}</h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none p-2"
            type="button"
          >
            ×
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Category</label>
              <select
                value={form.category}
                onChange={(e) => setForm({...form, category: e.target.value, subcategory: ''})}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="income">Income</option>
                <option value="expense">Expense</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Subcategory</label>
              <select
                value={form.subcategory}
                onChange={(e) => setForm({...form, subcategory: e.target.value})}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="">Select subcategory</option>
                {subcategories[form.category]?.map(sub => (
                  <option key={sub} value={sub} className="capitalize">
                    {sub.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700">Description</label>
            <input
              type="text"
              required
              value={form.description}
              onChange={(e) => setForm({...form, description: e.target.value})}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g., Payment to ABC Landscaping"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Amount ($)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                required
                value={form.amount}
                onChange={(e) => setForm({...form, amount: e.target.value})}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Transaction Date</label>
              <input
                type="date"
                required
                value={form.transaction_date}
                onChange={(e) => setForm({...form, transaction_date: e.target.value})}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Vendor (Optional)</label>
              <input
                type="text"
                value={form.vendor}
                onChange={(e) => setForm({...form, vendor: e.target.value})}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., ABC Landscaping"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Reference # (Optional)</label>
              <input
                type="text"
                value={form.reference_number}
                onChange={(e) => setForm({...form, reference_number: e.target.value})}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., Invoice #12345"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Link to Budget Item (Optional)</label>
            <select
              value={form.budget_line_item_id || ''}
              onChange={(e) => setForm({...form, budget_line_item_id: e.target.value})}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Not linked to budget item</option>
              {budgetItems
                .filter(item => item.category === form.category && item.is_active !== false)
                .map(item => (
                  <option key={item.id} value={item.id}>
                    {item.description} ({item.subcategory ? item.subcategory.replace('_', ' ') : ''})
                  </option>
                ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Linking allows transaction to automatically update budget actual amounts
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700">Notes (Optional)</label>
            <textarea
              rows="3"
              value={form.notes}
              onChange={(e) => setForm({...form, notes: e.target.value})}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              placeholder="Additional notes about this transaction..."
            />
          </div>
          
          {/* Property Fields - Board Only */}
          {isBoard && (
            <div className="grid grid-cols-2 gap-4 p-4 bg-blue-50 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-gray-700">Property Identifier (Lot/Unit)</label>
                <input
                  type="text"
                  value={form.property_identifier || ''}
                  onChange={(e) => setForm({...form, property_identifier: e.target.value})}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Lot 123"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Property Address</label>
                <input
                  type="text"
                  value={form.property_address || ''}
                  onChange={(e) => setForm({...form, property_address: e.target.value})}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., 123 Main St"
                />
              </div>
            </div>
          )}
          
          <div className="flex justify-end space-x-3 pt-4 sticky bottom-0 bg-white border-t mt-4 -mb-5 -mx-5 px-5 py-4">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {loading ? (isEditing ? 'Updating...' : 'Recording...') : (isEditing ? 'Update Transaction' : 'Record Transaction')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Balance Sheet Item Modal Component
const BalanceSheetItemModal = ({ form, setForm, onSubmit, onClose, loading }) => {
  const categories = {
    asset: ['current_assets', 'fixed_assets', 'investments', 'other_assets'],
    liability: ['current_liabilities', 'long_term_liabilities', 'other_liabilities'],
    equity: ['retained_earnings', 'reserves', 'other_equity']
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onSubmit(e);
  };

  const handleClose = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9999]" onClick={handleClose}>
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Add Balance Sheet Item</h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
            type="button"
          >
            ×
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Item Type</label>
              <select
                value={form.item_type}
                onChange={(e) => setForm({...form, item_type: e.target.value, category: ''})}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="asset">Asset</option>
                <option value="liability">Liability</option>
                <option value="equity">Equity</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Category</label>
              <select
                value={form.category}
                onChange={(e) => setForm({...form, category: e.target.value})}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="">Select category</option>
                {categories[form.item_type]?.map(cat => (
                  <option key={cat} value={cat} className="capitalize">{cat.replace('_', ' ')}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700">Name</label>
            <input
              type="text"
              required
              value={form.name}
              onChange={(e) => setForm({...form, name: e.target.value})}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g., Checking Account, Reserve Fund, etc."
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Amount ($)</label>
              <input
                type="number"
                step="0.01"
                required
                value={form.amount}
                onChange={(e) => setForm({...form, amount: e.target.value})}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">As of Date</label>
              <input
                type="date"
                required
                value={form.as_of_date}
                onChange={(e) => setForm({...form, as_of_date: e.target.value})}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700">Notes (Optional)</label>
            <textarea
              rows="3"
              value={form.notes}
              onChange={(e) => setForm({...form, notes: e.target.value})}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              placeholder="Additional notes about this item..."
            />
          </div>
          
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {loading ? 'Creating...' : 'Create Item'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BudgetSystem;